import type { MessageKey } from './messages.en-US';

// zh-CN dictionary — professional inspection Chinese, not a temporary developer translation
// (Final Implementation Authority §21.3 / §21.4). Typed as Record<MessageKey, string>: a missing
// key or an extra key is a compile error, so the two dictionaries can never drift.
//
// Frozen inspector-result glossary (D-01 Owner CHANGE 2026-09-03):
//   Pass = 无异常 · Defect = 缺陷 · Unable to Inspect = 无法检查 · N/A = 不适用 ·
//   Pending / Uninspected = 未检查（同一业务状态）。「通过」「待检」已被否决，不得使用。

export const zhCN: Record<MessageKey, string> = {
  'nav.inspections': '验屋',
  'nav.areas': '区域',
  'nav.findings': '问题记录',
  'nav.sync': '同步',

  'shell.productName': 'AI Home Inspector',
  'launch.tagline': '验屋 · 记录 · 出报告',
  'launch.skip': '跳过',
  'shell.back': '返回',
  'shell.close': '关闭',
  'shell.cancel': '取消',
  'shell.done': '完成',
  'shell.loading': '载入中…',
  'shell.screenFailedTitle': '这个页面没能打开',
  'shell.screenFailedBody': '已有的验屋记录和照片都还在本机。先回首页，再从那里重新进入。',
  'shell.screenFailedHome': '回到首页',
  'shell.account': '账号',
  'shell.appLanguage': 'App 语言',
  'shell.reportLanguage': '报告语言',
  'shell.version': '版本',
  'shell.signedInAs': '已登录：{email}',
  'shell.currentProperty': '当前房屋',
  'shell.progress': '进度',
  'shell.noProperty': '尚未打开验屋',
  'shell.noInspectionTitle': '请先打开一条验屋',
  'shell.noInspectionBody': '区域与问题记录作用于当前正在进行的验屋。',
  'shell.goToInspections': '前往验屋',
  'shell.backToAreas': '返回区域',
  'shell.syncStatus': '状态',
  'shell.syncBody': '验屋、照片与问题记录在操作时即保存到本机。',

  'status.offlineSaved': '离线 · 已保存',
  'status.offlineLocal': '离线 · 本机',
  'status.localOnly': '仅本机',
  'status.synced': '已同步',
  'status.syncing': '同步中…',
  'status.syncPending': '待同步（{n}）',
  'status.syncFailed': '同步失败',
  'status.savedOnDevice': '已保存在本机',

  'result.pass': '无异常',
  'result.defect': '缺陷',
  'result.unableToInspect': '无法检查',
  'result.na': '不适用',
  'result.pending': '未检查',
  'result.uninspected': '未检查',

  'finding.finding': '问题记录',
  'finding.findings': '问题记录',
  'finding.title': '问题标题',
  'finding.observation': '现场观察',
  'finding.recommendation': '建议',
  'finding.responsibleTrade': '建议处理专业人员',
  'finding.reportSection': '报告章节',
  'finding.save': '保存问题',
  'finding.savedToReportDraft': '已保存至报告草稿',
  'finding.reportDraft': '报告草稿',
  'finding.inspect': '检查',
  'finding.finalReview': '最终复核',

  'photo.takePhoto': '拍照',
  'photo.chooseLibrary': '从相册选择',
  'photo.libraryShort': '相册',
  'photo.cameraShort': '拍照',
  'photo.addPhoto': '添加照片',
  'photo.addPhotoNote': '添加照片备注',
  'photo.notePrefix': '备注：',
  'photo.editNote': '编辑备注',
  'photo.open': '查看照片',
  'photo.removeFromItem': '从检查项移除',
  'photo.deletePhoto': '删除照片',

  'language.english': 'English',
  'language.chinese': '中文',
  'language.appLanguageBody': 'App 语言只影响菜单与控件；每份验屋各自保存报告语言。',
  'language.reportLanguageDefault': '报告语言默认 English（按每份验屋单独设置）。',

  'inspection.notFoundTitle': '找不到这条验屋记录',
  'inspection.notFoundBody': '它可能尚未创建成功。',
  'inspection.backToInspections': '回到我的验屋',
  'inspection.noClient': '未填客户',
  // ---- 真机走查 2026-10-27（B4 · E1 · E4）：首页套餐行的 AI 单数、AI 语言说明、英文单复数（中文两种写法相同）----
  'home.planAiUnits.one': 'AI 还剩 {n} 单',
  'home.planAiUnits.other': 'AI 还剩 {n} 单',
  'language.aiResultsNote': '已有的 AI 结果不会翻译成新语言；在区域里点「全部重新分析」才会用新语言重新生成，并会再用一次 AI。',
  'overview.chipFindings.one': '问题记录',
  'overview.chipFindings.other': '问题记录',
  'home.statInspections.one': '验屋',
  'home.statInspections.other': '验屋',
  'home.statReports.one': '已出报告',
  'home.statReports.other': '已出报告',
  'home.statFindings.one': '问题记录',
  'home.statFindings.other': '问题记录',
  'common.itemsInspected.one': '已检查 {done} / {n} 项',
  'common.itemsInspected.other': '已检查 {done} / {n} 项',
  'overview.areaProgress.one': '{n} 项中已完成 {done} 项',
  'overview.areaProgress.other': '{n} 项中已完成 {done} 项',
  'overview.areaDone.one': '已完成 · 共 {n} 项',
  'overview.areaDone.other': '已完成 · 共 {n} 项',
  'comments.used.one': '用过 {n} 次',
  'comments.used.other': '用过 {n} 次',
  'comments.exported.one': '已导出 {n} 条评语',
  'comments.exported.other': '已导出 {n} 条评语',
  'layout.added.one': '已添加「{name}」，自带 {n} 项检查。',
  'layout.added.other': '已添加「{name}」，自带 {n} 项检查。',
  'area.analyze.one': 'AI 分析（{n} 张照片）',
  'area.analyze.other': 'AI 分析（{n} 张照片）',
  'area.completeBlocked.one': '还有 {n} 项未检查，完成前请逐项给出结果。',
  'area.completeBlocked.other': '还有 {n} 项未检查，完成前请逐项给出结果。',
  'area.wordingDone.one': '本区报告语已写好（{n} 项）。',
  'area.wordingDone.other': '本区报告语已写好（{n} 项）。',
  'area.wordingDoneKept.one': '本区报告语已写好（{n} 项）；你自己改过的 {kept} 项保留原样。',
  'area.wordingDoneKept.other': '本区报告语已写好（{n} 项）；你自己改过的 {kept} 项保留原样。',
  'area.aiNewPhotos.one': '分析新增的 {n} 张照片',
  'area.aiNewPhotos.other': '分析新增的 {n} 张照片',
  'area.aiPhase.analyzing.one': '正在分析 {n} 张照片…',
  'area.aiPhase.analyzing.other': '正在分析 {n} 张照片…',
  'area.aiRedoBody.one': '这一区的 {n} 张照片全部重新分析，替换原来的 AI 建议。会再用一次 AI 分析（从这一单里扣；照片多时可能多算一单）。只想分析新加的照片，用「分析新增的照片」。',
  'area.aiRedoBody.other': '这一区的 {n} 张照片全部重新分析，替换原来的 AI 建议。会再用一次 AI 分析（从这一单里扣；照片多时可能多算一单）。只想分析新加的照片，用「分析新增的照片」。',
  'advanced.willAnalyze.one': '将分析当前检查项关联的 {n} 张照片，并结合：',
  'advanced.willAnalyze.other': '将分析当前检查项关联的 {n} 张照片，并结合：',
  'settings.downloadAllShared.one': '{n} 份 PDF 已放进分享窗口，请选「存储到文件」或存到电脑。',
  'settings.downloadAllShared.other': '{n} 份 PDF 已放进分享窗口，请选「存储到文件」或存到电脑。',
  'settings.downloadAllDownloaded.one': '已下载 {n} 份 PDF。',
  'settings.downloadAllDownloaded.other': '已下载 {n} 份 PDF。',
  'settings.downloadAllSkipped.one': '另有 {n} 份账户里的报告没能下载，请在「报告」页逐份打开保存。',
  'settings.downloadAllSkipped.other': '另有 {n} 份账户里的报告没能下载，请在「报告」页逐份打开保存。',
  'billing.redeemDone.one': '已兑换，加了 {n} 单。',
  'billing.redeemDone.other': '已兑换，加了 {n} 单。',
  'pool.deleteLinkedNote.one': '其中 {n} 张已关联到检查项或问题记录，这些关联会一起移除。',
  'pool.deleteLinkedNote.other': '其中 {n} 张已关联到检查项或问题记录，这些关联会一起移除。',
  'pool.deletedToast.one': '已删除 {n} 张照片',
  'pool.deletedToast.other': '已删除 {n} 张照片',
  'poolAi.unplaced.one': '{n} 张照片 AI 没能归到具体检查项，留在「未分类」，请手动关联。',
  'poolAi.unplaced.other': '{n} 张照片 AI 没能归到具体检查项，留在「未分类」，请手动关联。',
  'poolAi.retryAreas.one': '重新分析没完成的 {n} 个区域',
  'poolAi.retryAreas.other': '重新分析没完成的 {n} 个区域',
  'poolAi.notesMissing.one': '还有 {n} / {total} 张照片没有备注。',
  'poolAi.notesMissing.other': '还有 {n} / {total} 张照片没有备注。',
  'poolAi.notesHint.one': '{n} 张照片还没有备注：写上拍的是哪里、看到了什么，AI 分析更准确。',
  'poolAi.notesHint.other': '{n} 张照片还没有备注：写上拍的是哪里、看到了什么，AI 分析更准确。',
  'poolAi.uncertain.one': '{n} 张照片 AI 拿不准属于哪里：已按最可能的区域分析，但没有自动归档——它的观察和照片在检查项里显示为「AI 建议 · 待确认」，由你确认或改放。',
  'poolAi.uncertain.other': '{n} 张照片 AI 拿不准属于哪里：已按最可能的区域分析，但没有自动归档——它的观察和照片在检查项里显示为「AI 建议 · 待确认」，由你确认或改放。',
  'poolAi.areaFailed.one': '{n} 张照片 · 没分析成：{reason}',
  'poolAi.areaFailed.other': '{n} 张照片 · 没分析成：{reason}',
  'poolAi.doneLine.one': '{n} 张照片已归入 {areas}。点区域查看 AI 的观察和建议报告语，确认或修改。',
  'poolAi.doneLine.other': '{n} 张照片已归入 {areas}。点区域查看 AI 的观察和建议报告语，确认或修改。',
  'report.generateSub.one': '{n} 份 PDF · 生成后锁定',
  'report.generateSub.other': '{n} 份 PDF · 生成后锁定',
  'report.areasInspected.one': '检查区域 {n} 个',
  'report.areasInspected.other': '检查区域 {n} 个',
  'report.findingsCount.one': '问题记录 {n} 条',
  'report.findingsCount.other': '问题记录 {n} 条',
  'report.limitationsCount.one': '无法检查 {n} 项',
  'report.limitationsCount.other': '无法检查 {n} 项',
  'report.itemResults.one': '共 {n} 项：无异常 {pass} · 缺陷 {defect} · 无法检查 {unable} · 不适用 {na}',
  'report.itemResults.other': '共 {n} 项：无异常 {pass} · 缺陷 {defect} · 无法检查 {unable} · 不适用 {na}',
  'report.morePhotos.one': '另有 {n} 张现场照片未放入报告。',
  'report.morePhotos.other': '另有 {n} 张现场照片未放入报告。',
  'move.body.one': '会把这一单（{n} 张照片、检查记录、协议）导出成一个文件，约 {size}。用隔空投送（iPhone）、Quick Share（安卓）、网盘或数据线传到另一台设备，再在那台设备上点「从文件导入一单」。',
  'move.body.other': '会把这一单（{n} 张照片、检查记录、协议）导出成一个文件，约 {size}。用隔空投送（iPhone）、Quick Share（安卓）、网盘或数据线传到另一台设备，再在那台设备上点「从文件导入一单」。',
  'importFile.done.one': '已导入「{address}」（{n} 张照片），可以在这台设备上继续了。',
  'importFile.done.other': '已导入「{address}」（{n} 张照片），可以在这台设备上继续了。',
  'drafts.nearBody.one': '没出正式报告、90 天没有改动的草稿会自动清除，App 里的照片和记录一起删除，手机相册里的照片不受影响。最早一份还剩 {n} 天：{address}。',
  'drafts.nearBody.other': '没出正式报告、90 天没有改动的草稿会自动清除，App 里的照片和记录一起删除，手机相册里的照片不受影响。最早一份还剩 {n} 天：{address}。',
  'inspections.areasDone.one': '区域 {done}/{n}',
  'inspections.areasDone.other': '区域 {done}/{n}',
  'poolAi.analyzing.one': '正在分析：{areas}（已完成 {done} / {n} 个区域）',
  'poolAi.analyzing.other': '正在分析：{areas}（已完成 {done} / {n} 个区域）',
  'poolAi.analyzingShort.one': '正在逐区分析（已完成 {done} / {n} 个区域）',
  'poolAi.analyzingShort.other': '正在逐区分析（已完成 {done} / {n} 个区域）',
  'report.itemsInspected.one': '{n} 项中 {done} 项有结果',
  'report.itemsInspected.other': '{n} 项中 {done} 项有结果',
  'sync.resetLedger.one': '已按 Owner 授权清空旧验屋数据（{date}）：{n} 条记录',
  'sync.resetLedger.other': '已按 Owner 授权清空旧验屋数据（{date}）：{n} 条记录',
  'inspection.title': '验屋',

  'common.required': '必填',
  'common.optional': '可选',
  'common.select': '选择',
  'common.more': '更多',
  'common.dragToReorder': '拖动排序',
  'common.collapse': '收起',
  'common.adjust': '调整',
  'common.decrease': '减少{label}',
  'common.increase': '增加{label}',
  'common.remaining': '剩余 {n} 项',
  'common.percent': '{n}%',
  'common.areasCount.one': '{n} 个区域',
  'common.areasCount.other': '{n} 个区域',
  'common.findingsCount.one': '{n} 条问题记录',
  'common.findingsCount.other': '{n} 条问题记录',
  'common.photosCount.one': '{n} 张照片',
  'common.photosCount.other': '{n} 张照片',
  'common.itemsCount.one': '{n} 项',
  'common.itemsCount.other': '{n} 项',
  'common.save': '保存',
  'common.delete': '删除',
  'common.add': '添加',
  'common.open': '打开',
  'common.autosaved': '已自动保存 {time}',
  'common.autosaveHint': '修改会自动保存。',
  'common.saveFailed': '保存失败：{message}',

  'areaKind.exterior': '外部 / 场地',
  'areaKind.roof': '屋顶',
  'areaKind.garage': '车库',
  'areaKind.basement': '地下室 / 爬行空间',
  'areaKind.structure': '结构',
  'areaKind.electrical': '电气系统',
  'areaKind.plumbing': '水管系统',
  'areaKind.waterHeater': '热水器',
  'areaKind.hvac': '供暖 / 空调',
  'areaKind.attic': '阁楼',
  'areaKind.interior': '室内 / 起居空间',
  'areaKind.kitchen': '厨房',
  'areaKind.bathroom': '卫生间',
  'areaKind.bedroom': '卧室',
  'areaKind.safety': '安全复核',
  'areaKind.other': '其他区域',
  'areaGroup.exterior': '外部',
  'areaGroup.spaces': '附属空间',
  'areaGroup.systems': '系统',
  'areaGroup.rooms': '房间',
  'areaGroup.wholeHouse': '整屋',
  'floor.1': '一楼',
  'floor.2': '二楼',
  'floor.3': '三楼',
  'floor.4': '四楼',
  'floor.5': '五楼',
  'floor.6': '六楼',
  'floor.7': '七楼',
  'floor.8': '八楼',
  'floor.n': '{n} 楼',
  'room.kitchen': '厨房',
  'room.living': '客厅',
  'room.family': '家庭房',
  'room.dining': '餐厅',
  'room.bedroom': '卧室',
  'room.fullBath': '全卫',
  'room.fullBathBase': '卫生间',
  'room.halfBath': '半卫',
  'room.laundry': '洗衣房',
  'space.garage': '车库',
  'space.detachedGarage': '独立车库',
  'space.basement': '地下室',
  'space.crawlspace': '爬行空间',
  'space.attic': '阁楼',
  'propertyType.singleFamily': '独立屋',
  'propertyType.multiFamily': '多户住宅',
  'propertyType.condo': '公寓 / Condo',
  'propertyType.townhouse': '联排 / Townhouse',
  'propertyType.other': '其他',
  'inspectionStatus.draft': '新建',
  'inspectionStatus.inProgress': '进行中',
  'inspectionStatus.completed': '已完成',
  'findingClass.defect': '缺陷',
  'findingClass.safety': '安全隐患',
  'findingClass.maintenance': '维护事项',
  'findingClass.advisory': '建议事项',
  'findingClass.informational': '说明',
  'findingStatus.draft': '草稿',
  'findingStatus.confirmed': '已入报告草稿',
  'findingStatus.rejected': '已撤回',
  'reason.obstructed': '被物品或装修遮挡',
  'reason.inaccessible': '无法进入 / 被锁',
  'reason.utilitiesOff': '系统未开启',
  'reason.unsafeCondition': '条件不安全',
  'reason.notInstalled': '无此项',
  'reason.other': '其他',
  'trade.licensedElectrician': '电工',
  'trade.licensedPlumbingProfessional': '水管专业人员',
  'trade.qualifiedHvacContractor': '暖通空调承包商 / 冷暖承包商',
  'trade.structuralEngineer': '专业工程师（PE）',
  'trade.qualifiedRoofingContractor': '屋顶承包商',
  'trade.licensedPestProfessional': '害虫 / 白蚁专业人员',
  'trade.other': '其他（请注明）',
  'trade.qualifiedGeneralContractor': '承包商（一般修缮）',
  'trade.qualifiedMasonryContractor': '砌体 / 水泥工程承包商',
  'trade.qualifiedSidingContractor': '外墙板承包商',
  'trade.qualifiedWindowDoorContractor': '门窗承包商',
  'trade.qualifiedGarageDoorTechnician': '车库门公司',
  'trade.qualifiedServiceTechnician': '设备维修技师',
  'trade.qualifiedChimneyProfessional': '烟囱专业人员',
  'trade.qualifiedInsulationContractor': '保温 / 隔热承包商',
  'trade.qualifiedWaterproofingContractor': '防水承包商',
  'trade.qualifiedDrainageGradingContractor': '园林 / 排水承包商',
  'trade.qualifiedPavingContractor': '铺装 / 混凝土承包商',
  'trade.licensedMoldAssessor': '纽约州持牌霉菌评估师',
  'priority.label': '影响 / 优先级',
  'priority.moderate': '中等',
  'priority.value': '优先级 {n}',

  'inspections.onDevice.one': '本机 {n} 条验屋',
  'inspections.onDevice.other': '本机 {n} 条验屋',
  'inspections.current': '当前验屋',
  'inspections.resume': '继续验屋',
  'inspections.new': '新建验屋',
  'inspections.recent': '最近记录',
  'drafts.nearTitle.one': '{n} 份草稿快要自动清除',
  'drafts.nearTitle.other': '{n} 份草稿快要自动清除',
  'drafts.keep': '保留',
  'drafts.clearedTitle.one': '已自动清除 {n} 份草稿',
  'drafts.clearedTitle.other': '已自动清除 {n} 份草稿',
  'drafts.clearedBody': '这些草稿 90 天没有改动，也没出正式报告：{list}。',
  'drafts.dismiss': '知道了',
  'drafts.noAddress': '未填地址',
  'assist.speak': '说话',
  'assist.listening': '正在听，点这里停止',
  'assist.switchLang': '切换说话的语言',
  'assist.langEn': '英',
  'assist.langZh': '中',
  'assist.keyboardMic': '点键盘上的麦克风说话，用的是手机自带的语音识别。',
  'assist.speechDenied': '没有麦克风或语音识别权限，请在手机设置里打开，或用键盘上的麦克风。',
  'assist.speechNoSpeech': '没听到说话，请再试一次。',
  'assist.speechFailed': '语音识别没有成功，请再试一次或用键盘上的麦克风。',
  'assist.library': '评语库',
  'assist.saveAsComment': '存为评语',
  'assist.saved': '已存为评语',
  'assist.exists': '评语库里已经有这一段',
  'assist.full': '评语库已满 500 条，请先删掉一些',
  'assist.emptyToSave': '先写一段话再存',
  'assist.pickerTitle': '我的评语',
  'assist.pickerSubtitle': '点一条加到正在写的地方',
  'assist.search': '搜索评语',
  'assist.pickerEmpty': '还没有评语。写好一段话后点「存为评语」，或在 设置 › 我的评语 里添加。',
  'comments.title': '我的评语',
  'comments.subtitle': '常用说法，写报告时一点就加入',
  'comments.intro': '你自己的常用说法，保存在这台设备上你的账户里。写任何一处文字时点「评语库」就能插入；也可以写好后点「存为评语」。',
  'comments.add': '添加评语',
  'comments.addPlaceholder': '例如 Recommend evaluation and repair by a licensed plumbing professional.',
  'comments.save': '保存',
  'comments.edit': '编辑',
  'comments.delete': '删除',
  'comments.deleteTitle': '删除这条评语？',
  'comments.deleteBody': '只删除评语库里的这一条，已经写进验屋记录的文字不受影响。',
  'comments.empty': '还没有评语。',
  'comments.count': '共 {n} 条',
  'comments.export': '导出评语',
  'comments.exportFile': '我的评语',
  'comments.exportFailed': '没能导出：{detail}',
  'comments.required': '请先写评语内容',
  'annotate.open': '标注',
  'annotate.edit': '修改标注',
  'annotate.title': '在照片上标注',
  'annotate.subtitle': '原照片不变，标注会印在报告的这张照片上',
  'annotate.tool': '标注方式',
  'annotate.arrow': '箭头',
  'annotate.circle': '圆圈',
  'annotate.box': '方框',
  'annotate.hint': '用手指在照片上拖动画出来。箭头从按下的地方指向松手的地方。',
  'annotate.limit': '一张照片最多 {n} 个标注',
  'annotate.undo': '撤销',
  'annotate.clear': '全部清除',
  'annotate.save': '保存标注',
  'guide.row': '拍照清单 · 已完成 {done} / {total}',
  'guide.title': '拍照清单',
  'guide.subtitle': '已完成 {done} / {total}',
  'guide.intro': '这类区域通常要拍的照片。拍了就打勾；没有这个部件或不需要拍，点右边说明一下。只是提醒，不会挡住完成区域或出报告。标「没有这个」的部件会告诉 AI，让它按缺失来判断。',
  'guide.taken': '已拍 {n} 张',
  'guide.absent': '没有这个',
  'guide.notNeeded': '不需要',
  'guide.open': '还没拍',
  'guide.camera': '拍照',
  'guide.library': '从相册选',
  'guide.markAbsent': '没有这个',
  'guide.markNotNeeded': '不需要',
  'guide.undo': '撤销',
  'inspections.emptyTitle': '还没有验屋记录',
  'inspections.emptyBody': '从新建验屋开始。你记录的一切都保存在本机。',
  'inspections.options': '验屋操作',
  'inspections.delete': '删除验屋',
  'inspections.deleteTitle': '删除「{address}」？',
  'inspections.deleteBody': '将删除这条验屋在本 App 内的全部记录（区域、检查项、问题记录、照片记录）。系统相册里的照片不受影响。此操作无法撤销。',
  'home.brand': 'AI Home Inspector',
  'inspections.clientLabel': '客户',
  'inspections.scheduledLabel': '预约时间',
  'inspections.progressLabel': '检查项进度',
  'inspections.scheduleOrNew': '预约 / 新建验屋',
  'inspections.clientLine': '客户：{name}',
  'inspections.finishedOn': '完成于 {date}',
  'nav.home': '首页',
  'home.greeting.morning': '早上好',
  'home.greeting.noon': '中午好',
  'home.greeting.afternoon': '下午好',
  'home.greeting.evening': '晚上好',
  'home.greetingNamed.morning': '早上好，{name}',
  'home.greetingNamed.noon': '中午好，{name}',
  'home.greetingNamed.afternoon': '下午好，{name}',
  'home.greetingNamed.evening': '晚上好，{name}',
  'home.license': '{state} 执照 · {number}',
  'home.licenseMissing': '还没绑定执照 · 去绑定',
  'home.focusEyebrow': '继续上次的验屋',
  'home.focusEmptyTitle': '今天从哪一栋开始？',
  'home.focusEmptyBody': '新建一条验屋，填好地址和客户就能开始。',
  'home.quickActions': '快捷入口',
  'home.allInspections': '全部验屋',
  'home.today': '今天',
  'home.todayEmpty': '今天没有安排的验屋',
  'home.upcoming': '接下来',
  'home.scheduleHint': '新建验屋时设好日期和时间，就会出现在这里。',
  'home.todo': '待办',
  'home.todoEmpty': '暂时没有要跟进的事',
  'home.todoReady': '检查项都已完成，可以收尾',
  'home.todoProfileTitle': '绑定执照',
  'home.todoProfile': '新建正式验屋要先绑定纽约州执照',
  'home.reportDue': '报告应于 {date} 前交付——纽约州要求验屋后五个工作日内交付（RPL §444-g(3)）。',
  'home.reportOverdue': '报告已于 {date} 到期——纽约州要求验屋后五个工作日内交付。',
  'home.month': '本月',
  'home.footer': '离线可用 · 记录只保存在本机 · 照片在系统相册',
  'home.footer.web': '网页版 · 记录只保存在这个浏览器 · 原图在你的电脑上',

  'setup.title': '新建验屋',
  'setup.property': '房屋',
  'setup.address': '房屋地址',
  'setup.addressPlaceholder': '例：123 Main St',
  'setup.cityPlaceholder': '例：Flushing',
  'setup.city': '城市',
  'setup.state': '州',
  'setup.zip': '邮编',
  'setup.propertyType': '房型',
  'setup.yearBuilt': '建造年份',
  'setup.weather': '天气',
  'setup.weatherPlaceholder': '例：晴，8°C',
  'setup.clientName': '客户姓名',
  'setup.clientNamePlaceholder': '客户或委托方',
  'setup.clientPhone': '客户电话',
  'setup.inspector': '验屋师',
  'setup.inspectorPlaceholder': '你的姓名（本机会记住）',
  'setup.create': '创建验屋',
  'setup.createSub.one': '{n} 个区域 · 自带检查项',
  'setup.createSub.other': '{n} 个区域 · 自带检查项',
  'setup.needArea': '至少要有一个区域才能开始检查。',
  'setup.hint': '创建后可以随时在「房屋布局」里添加、停用或改名区域。',
  'plan.createsBefore': '将创建',
  'plan.emptyCreatesBefore': '还没设房屋布局，现在只会创建',
  'plan.emptyCreatesAfter': ' 个整屋系统区域（结构、电气、水管等）；设好布局后再加上各楼层、房间和附属空间。',
  'plan.createsAfter.one': ' 个区域，自带检查项；创建后仍可增减。',
  'plan.createsAfter.other': ' 个区域，每个自带检查项；创建后仍可增减。',
  'plan.noSpaces': '没有附属空间',
  'plan.allSystems': '全部 {n} 项',
  'plan.noSystems': '全部关闭',
  'plan.without': '不含 {list}',
  'plan.noRooms': '还没有房间',
  'plan.extraPlaceholder': '其他房间，如 书房 / 阳光房',
  'plan.remove': '移除 {name}',
  'plan.floorsCount.one': '{n} 层',
  'plan.floorsCount.other': '{n} 层',
  'plan.bedCount': '{n} 卧',
  'plan.bathCount': '{n} 卫',
  'plan.kitchenCount': '{n} 厨',

  'layout.title': '房屋布局',
  'layout.hint': '只安排实际要检查的区域。每个区域的检查项会自动加载。',
  'layout.done': '完成',
  'layout.addArea': '添加区域',
  'layout.addFloorOrCustom': '添加楼层或自定义区域',
  'layout.optionCustom': '自定义区域',
  'layout.dragHandle': '拖动调整「{name}」的顺序',
  'layout.deleteBlockedTitle': '不能删除「{name}」',
  'layout.disableInstead': '停用这个区域',
  'layout.suggestedTitle': '建议布局',
  'layout.suggestedBody': '按{type}建议：{floors} · {areas}。',
  'layout.useSuggested': '使用建议布局',
  'layout.adjustLater': '稍后调整',
  'layout.edit': '编辑房屋布局',
  'layout.areaOptions': '区域操作',
  'layout.rename': '改名',
  'layout.moveToFloor': '移到其他楼层',
  'layout.moveUp': '上移',
  'layout.moveDown': '下移',
  'layout.disable': '停用',
  'layout.enable': '启用',
  'layout.disabledTag': '已停用',
  'layout.deleteTitle': '删除「{name}」？',
  'layout.deleteBody': '这个区域没有任何检查记录。删除后它和它的检查项一起移除，无法撤销。',
  'layout.deleteBlocked': '「{name}」已有检查记录（{data}），不能删除；房子没有的区域请停用。',
  'layout.addAreaTitle': '添加区域',
  'layout.areaName': '区域名称',
  'layout.areaNamePlaceholder': '可选，默认「{name}」',
  'layout.floor': '楼层',
  'layout.newFloor': '新增{floor}',
  'layout.noFloor': '不分楼层',
  'layout.addNamed': '添加「{name}」',
  'layout.addFloorBody': '楼层由它的区域构成。添加新楼层的第一个区域，或添加附属建筑。',
  'layout.optionNewFloor': '新楼层：{floor}',
  'layout.optionOutbuilding': '附属建筑（独立车库、棚屋…）',
  'layout.renameTitle': '区域改名',
  'layout.moveTitle': '把「{name}」移到',
  'layout.presetRooms': '房间',
  'layout.presetSpaces': '附属空间',
  'layout.presetSystems': '系统',
  'layout.presetOther': '自定义',
  'layout.data.settled.one': '{n} 项已处理',
  'layout.data.settled.other': '{n} 项已处理',
  'layout.data.annotated.one': '{n} 项有备注 / 关联',
  'layout.data.annotated.other': '{n} 项有备注 / 关联',
  'layout.data.areaNote': '有区域备注',
  'layout.data.none': '没有检查记录',
  'layout.allDisabled': '所有区域都已停用；在房屋布局里启用或添加。',

  'overview.progress': '整体进度',
  'overview.resume': '继续验屋',
  'overview.allInspected': '所有检查项都已给出结果',
  'overview.details': '验屋信息与备注',
  'overview.areas': '区域',
  'overview.chipUninspected': '未检查',
  'overview.chipUnable': '无法检查',
  'overview.reportStatus': '报告状态',
  'overview.photosCount.one': '{n} 张照片',
  'overview.photosCount.other': '{n} 张照片',
  'details.title': '验屋信息',
  'details.address': '地址',
  'details.cityStateZip': '城市 / 州 / 邮编',
  'details.typeYear': '房型 / 年份',
  'details.client': '客户',
  'details.inspector': '验屋师',
  'details.weather': '天气',
  'details.notes': '验屋备注',
  'details.notesPlaceholder': '整体说明、到场情况、占用情况等。',
  'details.status': '验屋状态',
  'details.markComplete': '标记验屋完成',
  'details.completeBlocked': '所有启用的区域完成后才能标记验屋完成。',
  'details.reopen': '验屋已完成 · 重新打开修改',

  // 2026-10-28 area page
  'area.unclassifiedShow': '查看',
  'area.unclassifiedHide': '收起',
  'area.title': '区域详情',
  'area.completedSubtitle': '区域已完成 · 仍可修改',
  'area.progressTitle': '{name}进度',
  'area.photos': '区域照片',
  'area.photosEmpty': '还没有区域照片。拍一张，或开始完整区域扫描。',
  'area.aiEyebrow': '可选 AI 辅助',
  'area.aiNeedsPhotos': '添加区域照片后才能使用 AI',
  'area.skipAi': '跳过 AI / 开始检查',
  'area.aiSkipped': '已跳过 AI · 稍后分析',
  'area.showAi': '显示 AI 选项',
  'area.items': '{name}检查项',
  'area.itemsHint': '点任一项即可直接给出结果',
  'area.filterAll': '全部',
  'area.filterLimited': '无法检查 / 不适用',
  'area.noPending': '这个区域没有未检查的项目了。',
  'area.noMatch': '没有符合筛选的项目。',
  'area.otherFindingsEmpty': '清单以外的发现记在这里，记录后仍留在本区域。',
  'area.notePlaceholder': '这个区域的整体说明、限制、材料等。',
  'area.complete': '完成区域',
  'area.reopen': '区域已完成 · 重新打开修改',
  'area.blockers.one': '还有 {n} 项未检查',
  'area.blockers.other': '还有 {n} 项未检查',
  'area.notFound': '找不到这个区域',
  'area.notFoundBody': '它可能尚未创建成功。',
  'area.clearResult': '清除结果',
  'area.clearResultTitle': '清除这一项的结果？',
  'area.clearResultBody': '这一项将回到未检查。备注、照片与问题记录都会保留。',

  'photo.albumSaved': '已存相册',
  'photo.albumFailed': '相册保存失败',
  'photo.albumUnavailable': '此平台无相册',
  'photo.albumPending': '相册保存中…',
  'photo.imported': '已导入',
  'photo.albumFailedDetail': '照片已存入 App，但相册保存失败：{detail}',
  'photo.albumUnavailableDetail': '照片已存入 App。{detail}',
  'home.webNoticeTitle': '网页版：没做完的验屋只保存在这台电脑的这个浏览器里',
  'home.webNoticeBody': '不要用无痕窗口，不要清除这个网站的数据；一单在哪台设备开始就在哪台做完，要换设备就在「验屋」列表里用「搬到另一台设备」导出文件。生成正式报告后，报告 PDF 会存到账户里，手机上也能打开。',
  'home.webNoticeNotKept': '这个浏览器还没答应长期保存本网站的数据，空间不够时可能被清理。把本网站加入书签，或在地址栏点「安装」装到电脑上，浏览器就会长期保存；做完一单尽快生成报告。',
  'photo.webNoAlbum': '照片已加入。网页版不另存相册，原图仍在你电脑上选取它的位置。',
  'photo.noteTitle': '照片备注',
  'photo.notePlaceholder': '这张照片拍的是什么、位置、需要记住的情况。',
  'photo.opening': '正在打开相机…',
  'photo.importing': '导入中…',
  'photo.viewer': '照片查看',
  'photo.counter': '第 {index} / {total} 张',
  'photo.previous': '上一张',
  'photo.next': '下一张',
  'photo.deleteTitle': '删除这张照片的 App 记录？',
  'photo.deleteBody': '只删除本 App 内的照片记录和关联；系统相册里的照片不会被删除。',
  'photo.deleteConfirm': '删除 App 记录',
  'photo.sourceCamera': '拍照',
  'photo.sourceLibrary': '相册导入',
  'photo.albumStatePending': '相册保存中',
  'photo.albumStateSaved': '已存系统相册',
  'photo.albumStateFailed': '相册保存失败',
  'photo.albumStateUnavailable': '此平台无系统相册',
  'photo.albumStateNotRequired': '相册原图（导入）',

  'item.metaOf': '第 {index} / {total} 项 · {area}',
  'item.status': '检查结果',
  'item.reportWording': '报告语（预览）',
  'item.reportWordingNone': '这个结果不产生单独的报告语：无法检查与不适用由报告的对应栏目措辞。',
  'item.reportWordingWriting': 'AI 正在写…',
  'item.reportWordingWritten': '这一项的报告语已写好。',
  'item.reportWordingKept': 'AI 写的时候你改了这一项，保留你写的。',
  'area.generateWording': '生成本区报告语',
  'area.wordingStarted': '正在为本区已检查的项写报告语…',
  'area.wordingNothing': '本区还没有已检查的项，没有报告语要写。',
  'area.wordingAllKept': '本区已检查的项都是你自己改过的报告语（{kept} 项），保留原样。',
  'item.hint': '检查要点',
  'item.scopeSupplemental': '补充观察 · 超出纽约验屋最低标准，不是法定必查项',
  'item.scopeMethod': '观察方法 / 检查限制记录',
  'item.alarmTested': '已按测试键测试',
  'item.alarmTestedHint': '不勾 = 只记录在位，报告写「未做功能测试」。',
  'item.testedGfci': '已测试（测试键或测试仪）',
  'item.testedOutlets': '已抽测',
  'item.testedWindowsDoors': '已抽查开合',
  'item.testedEgress': '已试开窗户',
  'item.testedGarageDoor': '已测试遇阻反弹',
  'item.testedBathExhaust': '已试运行排风扇或开窗',
  'item.testedHint': '不勾 = 只记录观察，报告不写已测试 / 已试开。',
  'item.reason': '原因',
  'item.inspectorNote': '验屋师备注',
  'item.inspectorNoteHint': '验屋师自用，不会自动变成问题记录。',
  'item.notePlaceholder': '记录看到的情况。',
  'item.unableNotePlaceholder': '说明现场为何无法检查。',
  'item.optionalNote': '可选备注',
  'item.naNotePlaceholder': '为何不适用（可选）。',
  'item.evidenceEmpty': '这一项还没有证据照片。',
  'item.nextItem': '下一项',
  'item.noNext': '没有未检查的项了',
  'item.lastUnchecked': '这是最后一项，选好结果就全部检查完了',
  'item.incomplete': '未完成',
  'item.unableIncomplete': '无法检查需要选择原因。',
  'item.saved': '已保存在本机',

  'finding.editTitle': '编辑问题记录',
  'finding.newTitle': '新问题记录',
  'finding.otherTitle': '其他发现',
  'finding.titlePlaceholder': '例：主水阀阀体锈蚀',
  'finding.observationPlaceholder': '看到了什么、在哪里。',
  'finding.recommendationPlaceholder': '建议……（写明由哪类专业人员处理）',
  'finding.tradeOther': '注明工种',
  'finding.tradeOtherPlaceholder': '例：持证烟囱专业人员',
  'finding.saveSub': '（保存至报告草稿）',
  'finding.discardTitle': '放弃修改？',
  'finding.discardBody': '这条问题记录未保存的修改会丢失；已拍的照片仍留在区域照片里。',
  'finding.discard': '放弃',
  'finding.keepEditing': '继续编辑',
  'finding.draftStatus': '本机草稿 · 尚未进入报告草稿',
  'finding.savedStatus': '已保存至本机报告草稿',
  'finding.withdraw': '撤回此问题记录',
  'finding.restore': '恢复此问题记录',
  'finding.deleteRecord': '删除问题记录',
  'finding.deleteTitle': '删除这条问题记录？',
  'finding.deleteBody': '只删除问题记录本身；关联的照片仍保留在区域照片里。',
  'finding.minimum': '保存需要填写标题与现场观察。',
  'finding.exportIncomplete': '导出报告前需补齐建议、处理工种与报告章节。',

  'findings.empty': '尚无问题记录',
  'findings.otherFinding': '其他发现',
  'findings.drafts.one': '{n} 条草稿尚未保存至报告草稿',
  'findings.drafts.other': '{n} 条草稿尚未保存至报告草稿',

  'sync.onDevice': '本机数据',
  'sync.inspections.one': '{n} 条验屋',
  'sync.inspections.other': '{n} 条验屋',
  'sync.notEnabled': '本版本未启用云同步。所有数据保存在本机；现场工作从不等待网络。',

  // ---- Mobile Owner Test build (2026-09-04): auth · settings · agreement · import / pool · area · closeout · report ----
  'nav.reports': '报告',
  'nav.settings': '设置',
  'inspection.noAddress': '未填地址',
  'common.areasKindsCount.one': '纳入 {n} 类区域',
  'common.areasKindsCount.other': '纳入 {n} 类区域',
  'reason.weather': '天气 / 环境限制',
  'details.created': '创建时间',
  'item.commentShort': '评语',
  'auth.heroTitle': '从验屋到出报告，省下 30–50% 的时间。',
  'auth.heroSubtitle': '逐区清单带着走，不漏项；拍照即记录，AI 可代笔描述。第一次验屋，也能当天交出专业报告。',
  'auth.heroTags': '离线可用 · AI 可选 · 新手当天上手',
  'auth.email': '邮箱',
  'auth.password': '密码',
  'auth.confirmPassword': '确认密码',
  'auth.newPassword': '新密码',
  'auth.code': '邮件中的验证码',
  'auth.signIn': '登录',
  'auth.createAccount': '创建账号',
  'auth.createTitle': '创建账号',
  'auth.createBody': '验屋前需要登录。创建后会收到一封验证邮件。',
  'auth.haveAccount': '已有账号，去登录',
  'auth.forgot': '忘记密码？',
  'auth.forgotTitle': '重置密码',
  'auth.forgotBody': '我们会把验证码发到你的邮箱；下一步输入验证码和新密码。',
  'auth.sendReset': '发送重置验证码',
  'auth.resetTitle': '设置新密码',
  'auth.resetBody': '输入发送到 {email} 的验证码和新密码。',
  'auth.setPassword': '设置密码并登录',
  'auth.verifyTitle': '验证邮箱',
  'auth.verifyBody': '输入我们发送到 {email} 的验证码；收不到请查看垃圾邮件。',
  'auth.verify': '验证并登录',
  'auth.resend': '重新发送邮件',
  'auth.backToSignIn': '返回登录',
  'auth.or': '或',
  'auth.apple': '使用 Apple 登录',
  'auth.google': '使用 Google 登录',
  'auth.show': '显示',
  'auth.hide': '隐藏',
  'auth.showPassword': '显示密码',
  'auth.hidePassword': '隐藏密码',
  'auth.passwordRules': '至少 {n} 个字符。',
  'auth.passwordMismatch': '两次输入的密码不一致。',
  'auth.notConfigured': '本版本未配置登录服务，无法登录。',
  'auth.offlineNoSession': '当前离线。登录需要网络；之前已登录有效的账号可离线继续使用。',
  'auth.requiredNote': '必须登录后才能验屋。登录后现场记录保存在本机，离线也可继续。',
  'auth.msg.ok': '登录成功。',
  'auth.msg.checkEmail': '请查收邮件中的验证码。',
  'auth.msg.resetSent': '重置验证码已发送，请查收邮件（含垃圾箱）。',
  'auth.msg.codeSent': '如果有账户用这个邮箱，登录验证码已经发出，请查收邮件（含垃圾箱）。',
  'auth.msg.tooSoon': '刚刚已发过验证码，请等一分钟再要。',
  // 网页版：在 App 里用 Apple 或 Google 建的账户从这里进（2026-10-20）。
  'auth.codeSignIn': '用邮箱验证码登录',
  'auth.codeSignInTitle': '用验证码登录',
  'auth.codeSignInBody': '我们把一次性验证码发到账户的邮箱。在 App 里用 Apple 或 Google 建的账户也可以这样登录。',
  'auth.sendCode': '发送验证码',
  'auth.msg.notConfigured': '本版本未配置登录服务。',
  'auth.msg.invalidCredentials': '邮箱或密码不正确。',
  'auth.msg.emailNotConfirmed': '该邮箱尚未验证，请输入验证邮件中的验证码。',
  'auth.msg.emailTaken': '该邮箱已有账号，请登录或重置密码。',
  'auth.msg.weakPassword': '密码太短或太简单。',
  'auth.msg.invalidCode': '验证码无效或已过期，请重新发送。',
  'auth.msg.network': '无法连接登录服务，请检查网络后重试。',
  'auth.msg.cancelled': '已取消。',
  'auth.msg.notNative': 'Apple / Google 登录在 App 内可用；网页端请用邮箱登录。',
  'auth.msg.googleUnavailable': 'Google 登录在这个版本里还没登记完成，暂时用不了。请先用邮箱登录。',
  'auth.msg.appleUnavailable': 'Apple 登录暂时用不了：请确认本机已登录 Apple ID（设置 › Apple 账户）；若已登录仍不行，是这个版本还没开通 Apple 登录，请先用邮箱或 Google 登录。',
  'auth.msg.unknown': '登录失败。',
  'settings.groupApp': '应用',
  'settings.groupAccount': '账号',
  'settings.groupLegal': '帮助与条款',
  'settings.languageBody': '默认跟随手机系统语言；只在这里修改。',
  'settings.languageSystem': '跟随系统',
  'settings.languageSystemBody': '正在跟随手机系统语言：当前为 {locale}。App 语言只影响菜单与控件。',
  'settings.account': '账号与安全',
  'settings.accountBody': '登录、密码、删除账号',
  'settings.sync': '同步与存储',
  'settings.syncBody': '本机数据与同步状态',
  'settings.photos': '照片与相机',
  'settings.photosBody': '相册规则、照片数量',
  'settings.plan': '套餐与订阅',
  'settings.planBody': '当前套餐、还剩几单、订阅说明',
  'settings.help': '帮助与支持',
  'settings.helpBody': '快速指南、版本信息',
  'settings.privacy': '隐私',
  'settings.privacyBody': '哪些数据留在本机、账号用于什么',
  'settings.terms': '条款',
  'settings.termsBody': 'App 使用条款与验屋服务条款',
  'settings.about': '关于',
  'settings.aboutBody': '版本、存储、数据完整性',
  'settings.signOut': '退出登录',
  'settings.signOutTitle': '退出登录？',
  'settings.signOutBody': '本机的验屋记录保留。继续工作需要重新登录。',
  'settings.signedInAs': '已登录',
  'settings.offlineContinuation': '离线中 —— 正在使用本机上次联网时有效的登录会话。',
  'settings.changePassword': '修改密码（发送重置验证码）',
  'settings.changePasswordSent': '重置验证码已发送到 {email}。退出登录后用「忘记密码」设置新密码。',
  'settings.deleteAccount': '删除账号',
  'settings.deleteBody': '删除服务器上的登录账号，账户里保存的报告 PDF 也一起删除。本机的验屋记录不会因此删除。删除前请先下载全部报告。',
  'settings.deleteTitle': '删除此账号？',
  'settings.deleteConfirmBody': '账号将在服务器上删除并退出登录，账户里保存的报告 PDF 一起删除，剩余的验屋次数和 AI 额度作废；此操作无法撤销。本机记录保留。自动续订的会员不会因此取消，请先在 App Store 或 Google Play 的订阅管理里取消。',
  'settings.downloadAll': '先下载全部报告',
  'settings.downloadAllWorking': '正在准备…',
  'settings.downloadAllNone': '本机和账户里都还没有报告。',
  'settings.downloadAllFailed': '没能完成：{detail}',
  'settings.deleteConfirm': '删除我的账号',
  'settings.deleteDone': '账号已删除，已退出登录。',
  'settings.deleteUnauthenticated': '请重新登录后再删除账号。',
  'settings.deleteUnavailable': '账号删除服务当前不可用（服务返回 {status}），账号与数据未改动。',
  'settings.deleteDenied': '删除请求被拒绝，账号与数据未改动。',
  'settings.deleteUnknown': '结果未知（连接中断）。请稍后重试；未经确认账号不会被删除。',
  'settings.photosRuleAlbum': 'App 内拍照成功后立即保存到系统相册，不等待 AI、上传或保存。',
  'settings.photosRuleImport': '从相册选入的照片不再另存。',
  'settings.photosRuleDelete': '在 App 内删除照片只删除 App 记录，绝不删除系统相册里的照片。',
  'settings.photosRuleNotes': '每张照片都可以加备注；备注行始终显示在照片下方。',
  'settings.photosRuleAlbum.web': '网页版从电脑里选照片导入，不另存副本；原图仍在你电脑上原来的位置。',
  'settings.photosRuleDelete.web': '在网页版里删除照片，只删除这个浏览器里的记录，绝不碰你电脑上的原图。',
  'settings.planOptions': '套餐',
  'settings.planSingle': '单次报告',
  'settings.planMonthly': '包月',
  'settings.planAnnual': '包年',
  'settings.planAddon': '加 3 单包',
  // ---- 套餐与订阅 / 购买（创始人 2026-09-11 裁决：D-3 加购包仅限订阅用户 · D-4 额度 = 价格的
  // 30%，显示「约剩 N 次」· D-5 没有套餐时每次正式验屋扣一次 $19.99 · X-2 额度不结转 ·
  // X-5 优惠码走 Apple 官方兑换页）。四个价位是 billingPolicy.ts 里的固定常量，不进词条。
  'billing.currentPlan': '当前套餐',
  'billing.planNone': '当前没有有效套餐',
  'billing.renewsOn': '{date} 续订',
  'billing.endsOn': '{date} 到期',
  'billing.remainingInspections.one': '还剩 {n} 单',
  'billing.remainingInspections.other': '还剩 {n} 单',
  'billing.remainingNote': '一单是一份验屋的 AI 分析，约 400 张照片以内；超过 400 张按两单计。',
  'billing.singleCredits.one': '单次购买还有 {n} 份未使用',
  'billing.singleCredits.other': '单次购买还有 {n} 份未使用',
  'billing.trialRemaining.one': '还有 {n} 份免费正式报告',
  'billing.trialRemaining.other': '还有 {n} 份免费正式报告',
  'billing.noAllowance': 'AI 单数已用完。',
  'billing.internalTester': '内部测试账号',
  'billing.internalTesterNote': '这个账号用于测试和审核，验屋和 AI 分析都不扣单。',
  'billing.lastSynced': '{time} 更新',
  'billing.singleBody': '一份正式报告，含这一单的 AI 分析（约 400 张照片以内）。不自动续费。',
  'billing.monthlyBody': '每月含 20 单 AI 分析，正式报告数量不限；没用完的不结转。',
  'billing.annualBody': '全年含 240 单 AI 分析（一个池子），正式报告数量不限；没用完的不结转。',
  'billing.addOnBody': '再加 3 单 AI 分析，仅限包月 / 包年用户。不过期。',
  'billing.buy': '在 App Store 购买',
  'billing.buying': '正在打开 App Store…',
  'billing.purchaseDone': '购买完成。',
  'billing.purchasePending': '购买处理中 —— 等待 Apple 完成。',
  'billing.purchaseCancelled': '已取消购买，未扣费。',
  'billing.purchaseFailed': '购买失败，未扣费。',
  'billing.notConfigured': '本版本未开通购买。',
  'billing.notConfiguredBody': '这个安装包里没有接入 App Store 购买，所以下面的购买、兑换和恢复按钮都不可用。已有的套餐和额度不受影响。',
  'billing.notEligible': '加 3 单包仅限包月 / 包年用户购买。',
  'billing.offline': '离线中 —— 联网后才能购买。',
  'billing.notSignedIn': '请重新登录后再购买。',
  'billing.simulated': '开发模拟商店 —— 不会产生任何扣费。',
  'billing.restore': '恢复购买',
  'billing.restoreDone': '已恢复购买。',
  'billing.restoreEmpty': '此 Apple ID 没有可恢复的购买。',
  'billing.manage': '管理订阅',
  'billing.manageBody': '将打开 App Store 的订阅设置。',
  'billing.offerCode': '兑换优惠码',
  'billing.offerCodeBody': '将打开 Apple 官方兑换页面。本页价格不会变化。',
  'billing.offerCodeCheck': '检查兑换结果',
  'billing.offerCodeWaiting': '请在 Apple 兑换页完成后回到这里检查结果。',
  'billing.offerCodeApplied': '优惠码已生效。',
  'billing.offerCodeNotDetected': '未发现新的购买。如果 Apple 提示优惠码无效，本 App 未做任何更改。',
  'billing.terms': '订阅说明',
  'billing.terms1': '包月 $99、包年 $999.99 由你的 Apple ID 自动续订，直到你取消为止。',
  'billing.terms2': '订阅含 AI 单数：包月每月 20 单，包年全年 240 单。一单是一份验屋的 AI 分析，约 400 张照片以内，超过按两单计。',
  'billing.terms3': '单数不结转：周期结束时没用完的单数作废。',
  'billing.terms4': '单次购买、加 3 单包和兑换码赠送的单数不过期。',
  'billing.terms5': '如需取消，请在周期结束前至少 24 小时到 Apple ID 设置中操作。App 内无法代为取消。',
  'billing.terms6': '确认购买时由你的 Apple ID 扣款。本 App 内从不填写任何银行卡信息。',
  'billing.eula': '使用条款（EULA）',
  // 安卓版的商店用语（2026-10-16 模拟器走查：安卓上按钮写着「在 App Store 购买」）。
  // 上面的同名词条是 iOS 用语，保持原样；安卓由 storeKey() 换成下面带 .android 的这一条。
  'billing.buy.android': '在 Google Play 购买',
  'billing.buying.android': '正在打开 Google Play…',
  'billing.purchasePending.android': '购买处理中 —— 等待 Google Play 完成。',
  'billing.notConfiguredBody.android': '这个安装包里没有接入 Google Play 购买，所以下面的购买、兑换和恢复按钮都不可用。已有的套餐和额度不受影响。',
  'billing.restoreEmpty.android': '此 Google 账号没有可恢复的购买。',
  'billing.manageBody.android': '将打开 Google Play 的订阅设置。',
  'billing.offerCodeBody.android': '将打开 Google Play 兑换页面。本页价格不会变化。',
  'billing.offerCodeWaiting.android': '请在 Google Play 兑换完成后回到这里检查结果。',
  'billing.offerCodeNotDetected.android': '未发现新的购买。如果 Google Play 提示优惠码无效，本 App 未做任何更改。',
  'billing.terms1.android': '包月 $99、包年 $999.99 在 Google Play 上由你的 Google 账号自动续订，直到你取消为止。',
  'billing.terms5.android': '可随时在 Google Play 的「付款和订阅 › 订阅」中取消，已付的周期结束前仍可使用。App 内无法代为取消。',
  'billing.terms6.android': '确认购买时由你的 Google Play 账号扣款。本 App 内从不填写任何银行卡信息。',
  // 网页版自己的付款通道（第 39 项）：RevenueCat Web Billing（Stripe）托管的付款页。
  'billing.buy.web': '去付款页购买',
  'billing.buying.web': '正在打开付款页…',
  'billing.purchasePending.web': '正在确认这笔付款，几秒到几分钟内这里会更新。',
  'billing.notConfiguredBody.web': '这个网页版没有接上付款服务，下面的购买按钮暂时不能用。在 App 里买的方案和单数在这里照常可用。',
  'billing.restoreEmpty.web': '这个账户还没有购买记录。',
  'billing.manageBody.web': '网页上买的订阅：在付款服务的页面在线取消或查看收据。在 iPhone 或安卓 App 里买的订阅，请在 App Store 或 Google Play 管理。',
  'billing.terms1.web': '网页上月付 $94、年付 $949（另加你所在地的销售税，付款页上单独列出），每期结束时按当时的价格自动续订，直到你取消。',
  'billing.terms5.web': '随时在本页点「管理订阅」在线取消，步骤和订阅时一样多；取消后用到已付期结束。续订后不按剩余天数退款（误扣款 30 天内告诉我们即退）。',
  'billing.terms6.web': '付款由 Stripe 经 RevenueCat 处理，卡号不经过这个网页和我们的服务器。购买后会收到确认邮件；年订阅续订前 15–45 天邮件提醒。',
  'billing.autoRenewConsent': '我已阅读并同意上面的自动续订条款：按期自动扣款，直到我取消。',
  'billing.autoRenewNeedConsent': '请先勾选同意自动续订条款。',
  'billing.consentNotRecorded': '同意记录没有保存成功，所以没有打开付款页。请稍后再试。',
  'billing.webPurchaseConfirmed': '付款已确认，方案已更新。',
  'billing.serverOffline': '离线中 —— 显示本机最后一次看到的额度。',
  'billing.serverUnavailable': '额度服务出错了，这些数字可能不是最新的。请稍后再试。',
  'billing.serverNotConfigured': '本版本未接入额度服务。',
  'billing.unfunded.one': '有 {n} 次离线创建的验屋没有匹配到购买。',
  'billing.unfunded.other': '有 {n} 次离线创建的验屋没有匹配到购买。',
  'billing.sheetTitle': '新建验屋需要套餐或单次购买',
  'billing.sheetBody': '正式验屋需要有效套餐，或一次尚未使用的单次购买。演示验屋始终免费。',
  'billing.sheetOffline': '离线状态下，没有套餐或单次购买就无法新建正式验屋。演示验屋不受影响。',
  'billing.aiQuotaExhausted': 'AI 单数已用完。订阅用户可在「设置 › 套餐与订阅」中买「加 3 单包」。',
  'billing.offlineNoCredit': '现在离线，而且没有可用的套餐或未使用的单次购买。联网后购买即可新建正式验屋；演示验屋不受影响。',
  'billing.countsUnavailable': '暂时确认不了你还有几次可用（次数由服务端记账，这次没能拿到结果）。请稍后再试；演示验屋不受影响。',
  'billing.addOnCta': '买「加 3 单包」',
  'billing.freeFirst': '前 3 份正式报告免费，不限时，不需要付款方式。',
  'billing.poolLine': '本期 {left} / {total} 单',
  'billing.extraLine': '加购 / 赠送还剩 {n} 单',
  'billing.periodMonthly': '/ 月',
  'billing.periodAnnual': '/ 年',
  'billing.periodOnce': '一次性',
  'billing.redeemTitle': '兑换码',
  'billing.redeemBody': '输入收到的兑换码，换成免费的 AI 单数，演示验屋可用。',
  'billing.redeemPlaceholder': '例如 ABCDE-FGHJK',
  'billing.redeemConsent': '我同意：用这些单数做的演示验屋，可以匿名用于统计 AI 的准确率。',
  'billing.redeemAction': '兑换',
  'billing.redeemInvalid': '兑换码不对，请检查后再输入。',
  'billing.redeemExpired': '这个兑换码已过期。',
  'billing.redeemAlready': '这个兑换码你已经用过了。',
  'billing.redeemUsedUp': '这个兑换码已经被用完了。',
  'billing.redeemNeedConsent': '请先勾选同意，才能兑换。',
  'billing.redeemFailed': '暂时兑换不了，请稍后再试。',
  'billing.planCta': '查看套餐与订阅',
  'billing.aiBillingUnavailable': '暂时无法确认 AI 额度。现场检查、照片与备注不受影响。',
  'settings.helpGuide': '快速指南',
  'settings.helpStep1': '新建验屋：房屋、客户、日期时间、验屋协议、房屋布局，以及起步方式。',
  'settings.helpStep2': '可以先把已拍好的照片导入照片池，也可以直接现场验屋。',
  'settings.helpStep3': '每个区域：每项一点即可 —— 无异常、缺陷、无法检查、不适用；需要时再拍照、选照片池或写评语。',
  'settings.helpStep4': '缺陷：标题、证据、观察、建议、保存。清单以外的问题用「+ 发现其他问题」。',
  'settings.helpStep5': '收尾检查只列出真正还缺的内容。全部功能离线可用。',
  'settings.helpStep6': '报告：最后选择报告语言并生成最终 PDF；生成后验屋记录锁定。',
  'settings.helpSupport': '支持',
  'settings.helpSupportBody': '遇到问题时，请说明所在页面、操作步骤和预期结果，这样最容易复现和解决。',
  'settings.helpBuild': '版本 {version}',
  // （Round 2 的五段隐私说明已于 2026-09-09 由创始人批准的 `settings.privacyNotice` 取代——批准草案 §6。）
  // App 自己的两条事实（批准稿没写、但用户看得见）：相册副本、分享去向。
  'settings.privacyAppFacts': 'App 内拍摄的照片会同时保存到手机系统相册；在 App 内删除照片不会删除相册副本。分享 PDF 时只会发送到你在分享面板中选择的位置。',
  'settings.privacyAppFacts.web': '网页版的验屋记录和照片只保存在这台电脑的这个浏览器里；删除照片不会删除你电脑上的原图。下载的 PDF 保存在浏览器的下载位置。',
  // 2026-09-25：完整政策在官网 /privacy-home-inspector（中英各一份，商店后台登记的就是它）；这里只放链接，不复制正文。
  'settings.privacyFullPolicy': '查看完整隐私政策（网页版，含英文）',
  'settings.termsApp': 'App 使用条款',
  'settings.termsFull': '查看完整服务条款（网页版，含英文，以英文为准）',
  'settings.termsApp1': '本 App 是给持证验屋师使用的现场工具。所有检查裁决由验屋师作出；App 负责记录、整理并输出验屋师的结论。',
  'settings.termsApp2': 'AI 功能只提供建议，未经验屋师确认不会改变任何检查结果或写入报告。',
  'settings.aboutDatabase': '数据库',
  'settings.aboutSchema': '数据 schema',
  'settings.aboutNamespace': '存储命名空间',
  'settings.aboutIntegrity': '数据完整性',
  'settings.aboutUpgraded': '启动时升级的记录',
  'settings.aboutDropped': '启动时无法读取而跳过的记录',
  'settings.aboutIntegrityNote': '无法读取的记录会被跳过而不是让 App 无法启动；其余记录不受影响。',
  'settings.aboutLegacy': 'AI Home Inspector 是全新的 App：验屋记录保存在这台手机上，其他 App 的记录不会出现在这里。',
  'setup.client': '客户',
  'setup.clientEmail': '客户邮箱',
  'setup.schedule': '验屋时间与验屋师',
  'setup.inspectionAt': '验屋日期时间',
  'setup.startTitle': '起步方式',
  'setup.startBody': '两种方式功能完全相同：AI 可选，检查清单由验屋协议决定。',
  'setup.startImportTitle': '导入已有照片',
  'setup.startImportBody': '已拍好的照片先全部进入照片池，再用 AI 或手动整理。',
  'setup.startLiveTitle': '现场验屋',
  'setup.startLiveBody': '逐区域走，一点给结果，边走边拍。',
  'setup.needStartMode': '请选择起步方式。',
  'setup.createImport': '创建并导入照片',
  'layout.basementDetails': '地下室详情',
  'layout.basementBody': '空间类型与装修状态分开记录。',
  'layout.basementType': '空间类型',
  'layout.basementFinish': '装修状态',
  'basementType.full': '全地下室',
  'basementType.partial': '半地下室',
  'basementType.crawlspace': '爬行空间',
  'basementType.slab': '平板地基',
  'basementType.none': '无',
  'basementFinish.unfinished': '未装修',
  'basementFinish.finished': '已装修',
  'basementFinish.partial': '部分装修',
  'basementFinish.mixed': '混合',
  'basementFinish.unknown': '无法判断',
  'agreement.title': '验屋协议',
  'agreement.view': '查看协议',
  'import.title': '导入已有照片',
  'import.eyebrow': '照片池',
  'import.heading': '照片先全部进入照片池',
  'import.body': '从相册选出这套房子已拍好的照片；之后再关联到检查项 —— 用 AI 或手动。',
  'import.noAiNote': '导入不依赖 AI；未经你要求不会分析任何照片。',
  'import.pick': '从相册选择照片',
  'import.pickMore': '继续添加照片',
  'import.imported.one': '已有 {n} 张照片进入照片池。',
  'import.imported.other': '已有 {n} 张照片进入照片池。',
  'import.empty': '照片池还没有照片。',
  'import.continue': '继续',
  'import.skip': '跳过导入，直接进入验屋',
  'import.choiceTitle': '照片处理方式',
  'import.inPool.one': '照片池 {n} 张',
  'import.inPool.other': '照片池 {n} 张',
  'import.choiceHeading': '照片已经进入照片池',
  'import.choiceBody': '可以让 AI 先把全部照片按区域和检查项归好、逐区分析；也可以不用 AI，自己逐项检查，每项从照片池挑照片。',
  'import.reading': '正在读取所选照片…',
  'import.progress': '正在导入照片 {done} / {total}',
  'import.progressHint': '照片较多时需要一点时间，请保持 App 打开，导入完会提示。',
  'import.manualTitle': '不用 AI，自己手动验屋',
  'import.manualBody': '逐项检查，为每项从照片池选照片；自己写评语、记录问题。',
  'import.aiRules': 'AI 不会替你作出检查结论，只提供建议，由你确认。',
  'pool.title': '照片池',
  'pool.titleShort': '照片池',
  'pool.open': '查看照片池',
  'pool.photos': '照片',
  'pool.select': '选择',
  'pool.filterAll': '全部',
  'pool.filterUnlinked': '未关联',
  'pool.filterLinked': '已关联',
  'pool.emptyTitle': '照片池是空的',
  'pool.emptyBody': '本次验屋的所有照片（导入的和区域内拍的）都会在这里，并可关联到任何检查项或问题记录。',
  'pool.linkSelected': '关联所选（{n}）',
  'pool.linkToArea': '关联到哪个区域？',
  'pool.linkToTarget': '选择检查项或问题记录',
  'pool.linkedToast': '已将 {n} 张关联到 {target}',
  'pool.linkedCount.one': '已关联 {n} 处',
  'pool.linkedCount.other': '已关联 {n} 处',
  'pool.removeLink': '移除关联',
  'pool.removeLinkTitle': '移除这个关联？',
  'pool.removeLinkBody': '照片仍留在照片池和相册中，只移除与「{target}」的关联。',
  'pool.unlinkedBanner.one': '照片池还有 {n} 张照片未关联',
  'pool.unlinkedBanner.other': '照片池还有 {n} 张照片未关联',
  'pool.unlinkedBannerBody': '可以从任一检查项，或直接在照片池中关联。',
  'pool.pickTitle': '从照片池选择',
  'pool.pickBody': '选择要关联的照片。一张照片可关联多个检查项或问题记录；不会移动或复制。',
  'pool.pickEmpty': '没有可关联的照片。',
  'pool.deleteSelected': '删除所选（{n}）',
  'pool.deleteManyTitle': '删除这 {n} 张照片的 App 记录？',
  'poolAi.eyebrow': 'AI 整理照片池',
  'poolAi.title': 'AI 分析全部照片',
  'poolAi.body': 'AI 先把照片池里还没归类的照片按区域和检查项归好，再逐个区域分析，给出观察和建议报告语。结果由你逐项查看、修改。',
  'poolAi.start.one': 'AI 分析全部照片（{n} 张）',
  'poolAi.start.other': 'AI 分析全部照片（{n} 张）',
  'poolAi.nothing': '照片池里没有待归类的照片。',
  'poolAi.sorting': '正在识别每张照片属于哪个区域、哪个检查项… {done} / {total} 张',
  'poolAi.liveHint': '请保持 App 打开，可以先去看别的页面。',
  'poolAi.estimate': '预计需要{duration}。分析时请保持 App 打开，可以先去看别的页面。',
  'poolAi.estimateMeasured': '按这台手机之前的实际速度，预计需要{duration}。分析时请保持 App 打开，可以先去看别的页面。',
  'poolAi.duration.minutes.one': '约 {n} 分钟',
  'poolAi.duration.minutes.other': '约 {n} 分钟',
  'poolAi.duration.underMinute': '不到 1 分钟',
  'poolAi.clock.minSec': '{m} 分 {s} 秒',
  'poolAi.clock.sec': '{s} 秒',
  'poolAi.elapsed': '已用 {time}',
  'poolAi.remaining': '预计还要{duration}',
  'poolAi.overrun': '比预计稍慢，仍在进行',
  'poolAi.doneTitle': 'AI 分析完成',
  'poolAi.doneNone': 'AI 没能认出这些照片是在哪里拍的，它们留在「未分类」，请手动关联。',
  'poolAi.areaLine': '{photos} 张照片 · {rows} 项有 AI 观察 · {unclassified} 条待归类',
  'poolAi.openUnclassified': '查看未分类照片',
  'poolAi.reviewHint': 'AI 不替你选检查结果。每条观察都带建议报告语：在检查项里选好结果后，可用「用 AI 观察生成报告语」写进报告。',
  'poolAi.failedTitle': 'AI 分析没有完成',
  'poolAi.failedNothingFiled': '照片没有被归类，可以再试一次。',
  'poolAi.showResult': '查看 AI 分析结果',
  'poolAi.notesTitle': '先给照片添加备注？',
  'poolAi.notesBody': '为所有照片添加备注，会提高 AI 分析效果。',
  'poolAi.notesAddNow': '现在去添加',
  'poolAi.notesAnalyzeNow': '直接 AI 分析',
  'poolAi.houseFactsHint': '房屋事实还没填（供暖燃料、供水、排污、地下室）。填上之后，AI 不会在每个区域反复问「这是油罐吗」。',
  'poolAi.houseFactsOpen': '去填房屋事实',
  'aiScan.title': 'AI 扫描此区域（可选）',
  'aiScan.helpLabel': 'AI 扫描是什么？',
  'aiScan.expand': '展开',
  'aiScan.collapse': '收起',
  'aiScan.help1': 'AI 扫描 = 把整个区域拍一遍：墙面、地面、天花、设备。每张照片立即保存，先入相册。',
  'aiScan.help3': '扫描是可选的。跳过它、或 AI 不可用，都不会阻塞检查、收尾或报告。',
  'aiScan.noPhotos': '还没有区域照片。',
  'area.otherIssues': '其他问题',
  'area.otherIssuesHint': '清单以外发现的问题，各自作为独立的问题记录。',
  'area.otherIssueCta': '+ 发现其他问题',
  'unable.body': '选择无法检查的原因；可以补一句说明。',
  'unable.note': '说明',
  'unable.notePlaceholder': '需要时补一句',
  'unable.confirm': '记为无法检查',
  'unable.noReason': '未选原因',
  'unable.change': '修改原因',
  'unable.choose': '选择原因',
  'finding.stepEvidence': '证据',
  'finding.stepObservation': '现场观察',
  'finding.stepRecommendation': '建议',
  'finding.recommendationHint': '导出报告前必填；写明由哪类专业人员处理。',
  'finding.reportSettings': '报告设置',
  'finding.tradeHint': '缺陷记录在报告中须写明处理工种。',
  'finding.structuralConcern': '结构性问题（验屋师确认）',
  'finding.structuralConcernHint': '位移 / 变形 / 明显开裂 / 疑似结构不稳 / 承重疑虑 → 转介专业工程师（PE）；普通表面劣化、砌体维修不勾。',
  'finding.tradeMissing': '未选工种',
  'closeout.title': '收尾检查',
  'closeout.open': '收尾检查',
  'closeout.openWithGaps': '收尾检查 · 还有 {n} 项待补',
  'closeout.blockingCount.one': '{n} 项阻塞报告',
  'closeout.blockingCount.other': '{n} 项阻塞报告',
  'closeout.blockingTitle': '报告前必须补齐',
  'closeout.blockingMeta': '只列真正未完成的内容',
  'closeout.advisoryTitle': '建议补充',
  'closeout.advisoryMeta': '不阻塞报告',
  'closeout.gapUninspected': '尚未给出结果',
  'closeout.gapUnableNoReason': '无法检查缺原因',
  'closeout.gapFindingNoRecommendation': '问题记录缺建议',
  'closeout.gapFindingNoTrade': '问题记录缺处理工种',
  'closeout.gapFindingNoPhoto': '问题记录没有照片',
  'closeout.fix': '去补',
  'closeout.readyTitle': '没有待补项',
  'closeout.readyBody': '所有检查项都有结果，全部问题记录已完整，可以生成最终报告。',
  'closeout.continueToReport': '进入报告',
  'report.title': '房屋验屋报告',
  'report.open': '报告',
  'report.statusTitle': '报告状态',
  'report.languageBody': '最后再选。英文：1 份英文 PDF。中文：1 份英文客户版 + 1 份中文自留版。',
  'report.variantClient': '英文客户版',
  'report.variantSelf': '中文自留版',
  'report.preview': '预览：{name}',
  'report.previewSubtitle': '与 PDF 内容一致',
  'report.generate': '生成最终报告',
  'report.generating': '正在生成…',
  // （Round 2 的生成确认已于 2026-09-09 由创始人批准的严格确认 `report.finalConfirm*` 取代——批准草案 §5.2。）
  'report.generatedToast.one': '已生成 {n} 份 PDF',
  'report.generatedToast.other': '已生成 {n} 份 PDF',
  'report.generateFailed': '报告没能生成。验屋内容都还在，可以再点一次「生成最终报告」；还是不行请联系我们。',
  'report.previewFailed': '预览没能打开，请再试一次。',
  'report.reviewAreaLocation': '全屋（{area}）',
  'finding.chineseInClientReport': '英文客户版会照印这一栏，生成报告前请改成英文。',
  'report.lockedTitle': '最终报告已生成，不可再编辑',
  'report.lockedBody': '本次验屋的工作数据已只读。生成的 PDF 保存在「报告」中。',
  'report.filesTitle': '已生成文件',
  'report.filesEmpty': '尚未生成 PDF',
  'report.filesCount.one': '{n} 份 PDF',
  'report.filesCount.other': '{n} 份 PDF',
  'report.pages.one': '{n} 页',
  'report.pages.other': '{n} 页',
  'report.share': '分享 / 保存',
  'report.shareFailed': '无法分享文件：{detail}',
  'report.downloaded': '已下载 {name}',
  'report.fileMissing': '本机找不到该 PDF 文件。',
  'reports.emptyTitle': '还没有报告',
  'reports.emptyBody': '每条验屋都会在这里显示其报告状态。',
  'reports.readyToGenerate': '可以生成',
  'report.selfCopy': '验屋师自留版 —— 不作交付',
  'report.propertyInformation': '房屋信息',
  'report.clientInformation': '客户信息',
  'report.inspectionDetails': '验屋信息',
  'report.agreement': '验屋协议',
  'report.fee': '费用',
  'report.feeAmount': '{amount} 美元',
  'report.summary': '概要',
  'report.limitedScopeStatement': '本报告反映验屋当日对可及区域与部件进行的有限、非侵入式目视检查，不是技术上穷尽的检查；隐蔽或无法进入的部位不在范围之内。氡气检测与害虫（含白蚁等蛀木昆虫）检查不属于纽约州住宅验屋：本次验屋不是白蚁 / 害虫检查，报告中记录的可见迹象仅为验屋当时所观察到的情况。霉菌及其他有害物质存在与否的判定、以及工程或建筑设计分析，不是纽约州验屋执业标准所要求，也不包含在本次验屋中。',
  'report.scopeStatement': '检查范围说明',
  'report.sections': '区域与系统',
  'report.systemsSummary': '同类检查汇总',
  'report.areaNote': '区域说明',
  'report.noFindings': '本区域无问题记录。',
  'report.observation': '现场观察',
  'report.recommendation': '建议',
  'report.responsibleTrade': '处理工种',
  'report.priority': '优先级',
  'report.severity': '级别',
  'report.defectSummary': '重大缺陷摘要',
  'report.defectSummaryNote': '本摘要不是完整报告。请阅读整份报告，包括各区域的观察、限制说明与检查范围。',
  'report.defectSummaryEmpty': '本次验屋未记录缺陷。',
  'report.defectSummaryCount': '{n} 条',
  'report.chineseGoverned': '本中文版仅供验屋师自留，不作交付；如与英文版不一致，以英文版为准。',
  'report.variantClientSummary': '客户中文摘要',
  'report.clientSummaryCopy': '中文摘要 · 给客户参考',
  'report.clientSummaryGoverned': '中文摘要仅供参考，以英文报告为准。',
  'report.clientSummaryDefectNote': '本摘要不是完整报告。请阅读英文报告全文，包括各区域的观察、限制说明与检查范围。',
  'report.clientSummaryUnableNote': '每一项的完整限制说明见英文报告的对应区域。',
  'report.clientSummaryToggle': '另出一份给客户的中文摘要',
  'report.clientSummaryToggleHint': '单独一份 PDF：重大缺陷摘要和没能检查的部分译成中文，页上注明「中文摘要仅供参考，以英文报告为准」。',
  'report.unableSeeArea': '详见各区域的限制说明。',
  'severity.title': '缺陷级别',
  'severity.safety': '安全 / 立即处理',
  'severity.repair': '修理 / 进一步评估',
  'severity.maintenance': '维护',
  'severity.suggestedHint': '这是按检查项给出的建议级别，可直接改。',
  'severity.chosenHint': '已按你的选择印在报告和摘要页上。',
  'report.evidence': '证据照片',
  'report.figure': '图 {n}',
  'report.unableSection': '无法检查 —— 限制说明',
  'report.reason': '原因',
  'report.note': '说明',
  'report.inspectorNotes': '验屋师备注',
  'report.terms': '本次验屋范围',
  'report.generatedAt': '生成时间',
  'report.page': '页',
  'report.address': '地址',
  'report.propertyType': '房型',
  'report.yearBuilt': '建造年份',
  'report.basement': '地下室',
  'report.client': '客户',
  'report.phone': '电话',
  'report.email': '邮箱',
  'report.inspectionDate': '验屋日期',
  'report.inspector': '验屋师',
  'report.weather': '天气',
  'report.notRecorded': '未记录',

  // ---- Owner Test Round 2 (2026-09-04): agreement · signatures · layout · real AI · pool · zoom ----
  'status.offline': '离线',
  'common.notSet': '未设置',
  'common.retry': '重试',
  'sync.storageTitle': '数据保存在哪里',
  'sync.storageBody': '验屋记录、检查结果、备注、问题记录和照片记录都保存在本机 App 自己的存储中；不会自动上传。',
  'sync.offlineBody': '没有网络时一切照常记录和保存；只有登录和 AI 分析需要联网。',
  'sync.aiBody': 'AI 分析只把你选中的照片、照片备注和区域整体备注经由我们的后端发送用于本次分析；结果保存在本机。',
  'sync.headerRule': '页眉只在需要注意时显示状态：离线、待同步或同步失败。',
  'inspections.lastEdited': '最近编辑 {time}',
  'setup.yearPlaceholder': '例：1998',
  'photo.addNote': '添加备注',
  'photo.noteSubtitle': '随照片保存 · AI 分析时一并发送',
  'photo.noteSave': '保存备注',
  'photo.noteHint': '备注会帮助 AI 判断，也会显示在照片池中。',
  'photo.pinchHint': '双指缩放 · 双击放大 · 左右滑动切换',
  'photo.zoomIn': '放大',
  'photo.zoomOut': '缩小',
  'photo.zoomReset': '适应屏幕',
  'pool.allPhotos': '全部照片',
  'pool.byArea': '按区域查看',
  'pool.groupEmptyTitle': '这里还没有照片',
  'pool.groupEmptyBody': '在该区域拍摄、或关联到该区域检查项与问题记录的照片会显示在这里。',
  'pool.indexHint': '照片按拍摄或关联的区域分组；一张照片可以属于多个区域。',
  'pool.unclassified': '未分类',
  'pool.unclassifiedHint': '尚未关联到任何区域的导入照片',
  'pool.unclassifiedEmptyBody': '所有照片都已关联区域。可以再导入照片，稍后整理。',
  'area.aiBusy': 'AI 分析正在进行中',
  'area.aiContinueManually': '继续手工检查',
  'area.aiFailed': '分析失败',
  'area.aiNotConfigured': '本版本未配置 AI 后端',
  'area.aiNotSignedIn': '登录后才能使用 AI 分析',
  'area.aiPhase.preparing': '正在准备照片…',
  'area.aiPhase.uploading': '上传中… {pct}%',
  'area.aiPhaseHint': '可以继续检查；照片和备注都已保存。',
  'area.aiReanalyze': '全部重新分析',
  'area.aiRedoTitle': '全部重新分析？会再用一次 AI',
  'area.aiRedoConfirm': '再用一次 AI，全部重新分析',
  'billing.addOnSheetTitle': '加 3 单包',
  'billing.addOnSheetBody': 'AI 单数用完了。加 3 单包再给 3 单 AI 分析，不过期。',
  'area.reportNote': '区域报告备注',
  'area.reportNoteHint': '会印在报告的该区域下',
  'area.scanNote': '区域整体备注',
  'area.scanNoteHint': '整体观察到的情况 —— 随区域保存，并作为 AI 分析的上下文。',
  'area.scanNotePlaceholder': '例：有霉味；水槽下柜子堆满杂物；热水器 2015 年……',
  'area.viewAllPhotos': '查看全部',
  'ai.concern': '可能的问题',
  'ai.question': 'AI 想确认',
  'ai.scopeNote': '范围说明',
  'ai.ignore': '忽略',
  'ai.ignoredToast': '已忽略',
  'ai.privacy': '只有点击分析时，照片、照片备注和区域整体备注才会经由我们的后端发送。',
  'ai.fail.network': '服务器无响应，请检查网络后重试。',
  'ai.fail.timeout': '分析超时，请减少照片后重试。',
  'ai.fail.aborted': '分析已取消。',
  'ai.fail.malformedResponse': '服务器返回的内容无法解析，请重试。',
  'ai.fail.unauthenticated': '登录状态失效，请重新登录后使用 AI。',
  'ai.fail.badRequest': '请求被拒绝，请重新打开区域后重试。',
  'ai.fail.tooLarge': '照片过大，请减少照片后重试。',
  'ai.fail.tooManyImages': '一次分析的照片过多，请减少选择。',
  'ai.fail.unsupportedImage': '有照片格式不受支持。',
  'ai.fail.rateLimited': 'AI 服务繁忙，请稍后重试。',
  'ai.fail.providerError': 'AI 服务商出错，请重试。',
  'ai.fail.providerRefused': 'AI 拒绝分析这些照片。',
  'ai.fail.unavailable': 'AI 分析暂时不可用。',
  'ai.fail.imageFailed': '有照片无法处理，无法上传。',
  'ai.fail.interrupted': '上次分析被中断，未完成。',
  'ai.fail.unknown': 'AI 分析失败。',
  // NY F1 R4（Owner 裁决 OD-13，2026-09-09）。范围提醒卡：AI 提到的、纽约验屋不要求的事项照样显示，
  // 但不是缺陷、不是任务、不进报告草稿，也不显示置信度。标题、正文与两个按钮取自 Owner 批准草案
  // （NY-AGREEMENT-V2-AND-REPORT-FLOW-APPROVED-DRAFT-20260909.md §8.1）逐字。
  'ai.scopeReminder.title': '不在纽约验屋范围内',
  'ai.scopeReminder.body': '纽约验屋标准规范不要求检查此项，它不属于本次验屋。如你想自己看一下，可把所见记在「其他发现」；报告会作为自愿观察打印。',
  'ai.scopeReminder.observationLabel': '你看到的情况',
  'ai.scopeReminder.savedToast': '已记入其他发现',
  // 检查限制提示：遮挡物本身不产生检查工作，被遮挡的、在范围内的部件才记录为会打印的检查限制（PP-22）。
  'ai.limitation.title': '该部件有一部分未能观察',
  'ai.limitation.body': '遮挡物本身不作检查。请把未能观察的部位记录在该部件的检查项上；报告会连同原因一并打印。',
  'ai.limitation.record': '记录检查限制',
  'ai.limitation.extentLabel': '未能观察的是哪一部分',
  'ai.limitation.savedToast': '已记录检查限制',
  // 卡片上的依据说明。措辞依据 NY-REPORT-AUTHORITY.md §4.12：纽约只是「不要求」的项目，
  // 绝不写成「纽约法律排除」。
  'ai.scopeNote.notRequiredNotIncluded': '纽约验屋标准规范不要求，且不包含在本次验屋中。',
  'ai.scopeNote.notPartOfNyHomeInspection': '不属于纽约验屋的范围。',
  'ai.scopeNote.clientExcluded': '应客户要求排除；报告会说明缩减后的范围。',
  'ai.scopeNote.uncertainSubject': 'AI 未能把它归入纽约验屋的系统，因此仅作观察处理。',
  'ai.scopeNote.presenceQuestion': '先确认房屋是否有此项，再决定是否成为检查工作。',
  'ai.scopeNote.limitationPrompt': '范围内的部件被遮挡——请记录为检查限制。',
  'ai.scopeNote.pestObservationOnly': '本次验屋不是白蚁 / 害虫检查；建议由持证白蚁 / 害虫防治专业人员检查。',
  'ai.scopeNote.sentenceRemoved': '已移除一句措辞：验屋不作法规符合性等结论。',
  'ai.fail.blockedNoScope': '这份协议签署时没有记下验屋范围（旧版本签的）。请重新签一次验屋协议，再使用 AI 分析。',
  'ai.fail.blockedNoScopeState': 'AI 分析目前只支持纽约州的房子：请在验屋资料里把房子所在州选为纽约（NY）。',
  'ai.fix.agreement': '去签协议',
  'ai.fix.property': '去填房子所在州',
  // 最终复核措辞提醒（RP-02）：只提醒，不阻断。
  'wording.title': '措辞提醒',
  'wording.body': '纽约验屋不作出这些结论。不阻断报告生成；如认同请自行修改。',
  'wording.reminderCount.one': '{n} 条措辞提醒',
  'wording.reminderCount.other': '{n} 条措辞提醒',
  'wording.source.areaName': '区域名称',
  'wording.source.itemTitle': '你添加的检查项',
  'wording.source.photoCaption': '照片备注',
  'wording.suggestion.P-01': '描述观察到的状况与安全隐患；建议由相应专业人员处理。',
  'wording.suggestion.P-02': '描述所见证据；验屋不判定使用是否合法。',
  'wording.suggestion.P-03': '本次验屋未判定产权归属与地界位置。',
  'wording.suggestion.P-04': '价值与是否购买的建议不属于验屋范围。',
  'wording.suggestion.P-05': '描述观察到的位移或劣化，并建议由持证专业工程师评估。',
  'wording.suggestion.P-06': '写成「疑似含有……的材料；本次验屋未作判定」，并建议相应专业人员处理。',
  'wording.suggestion.P-07': '剩余寿命、成因、费用与维修方法不属于验屋范围。',
  'wording.suggestion.P-08': '写明哪一部分未能观察及其原因；对被遮挡部分不作状况良好的描述。',
  'wording.suggestion.P-09': '按照片所见描述：「所提供照片中不可见，请现场确认」。',
  'wording.suggestion.P-10': '水质与燃气、一氧化碳检测不属于验屋范围。',
  'wording.suggestion.P-11': '按所见描述类型与铭牌参数；不评估容量与效率。',
  'wording.suggestion.P-12': '验屋不构成保修、担保或认证。',
  'wording.suggestion.P-13': '报告只描述房屋，不涉及客户或交易。',
  'wording.suggestion.P-14': '协议的法定条款由应用打印，报告中不复述。',
  'wording.suggestion.P-15': '只写窗户能否打开与观察到的限制；不判定逃生窗是否合规。',
  'wording.suggestion.P-16': '循环式抽油烟机只作配置记录，不是缺陷；缺陷只来自可见排风管的实际问题。',
  'layout.addMenu': '添加到房屋布局',
  'layout.addFloor': '添加楼层',
  'layout.addBasement': '添加地下室',
  'layout.addGarage': '添加车库',
  'layout.addAttic': '添加阁楼',
  'layout.addOther': '添加其他区域',
  'layout.atticBody': '阁楼入口及可见的结构、保温与通风。',
  'layout.basementNotSet': '类型未设置',
  'layout.emptyHint': '尚未设置。添加楼层、地下室、车库、阁楼或其他区域，或直接采用按房屋类型的建议。',
  'layout.finishNotSet': '装修状态未设置',
  'layout.finishOptional': '装修状态（可选）',
  'layout.floorEditSubtitle': '本层房间',
  'layout.garageAttached': '连体车库',
  'layout.garageDetached': '独立车库',
  'layout.otherName': '区域名称',
  'layout.otherPlaceholder': '例：泳池屋、工作间',
  'layout.present': '已包含',
  'layout.removeFloor': '删除此楼层',
  'layout.removeRow': '移除',
  'layout.rowEdit': '编辑{name}',
  'layout.suggestedDetail': '按房屋类型的预设；之后可以随意调整。',
  'layout.useSuggestedFor': '采用{type}的建议布局',
  'report.variantAgreement': '验屋协议（PDF）',
  'report.variantAddendum': '补充说明',
  'cloud.saved': '已存到账户',
  'cloud.pending': '等待存到账户',
  'cloud.deviceOnly': '只在本设备（客户没有同意存到账户）',
  'cloud.noteOffline': '现在没联网。这几份 PDF 在本机，联网后会自动存到账户。',
  'cloud.noteUnavailable': '账户存储暂时用不了。PDF 在本机，稍后会自动重试。',
  'cloud.noteNotConfigured': '账户存储还没开通，PDF 保存在本机。',
  'cloud.listTitle': '账户里的报告',
  'cloud.listMeta': '只读，换手机或重装后登录也能打开',
  'cloud.listEmpty': '账户里还没有报告。生成正式报告后会自动存进来。',
  'cloud.open': '打开',
  'cloud.openFailed': '打不开这份报告，请联网后再试。',
  'cloud.refresh': '刷新',
  'cloud.loading': '正在读取…',
  'cloud.loadingList': '正在读取账户里的报告…',
  'cloud.opening': '正在打开…',
  'addendum.cardTitle': '交付后发现错误',
  'addendum.cardBody': '原报告不能改。写一份带日期和签名的补充说明，写明更正哪一条、为什么。它和原报告一起存在账户里，一起发给客户。',
  'addendum.write': '写补充说明',
  'addendum.sheetTitle': '补充说明',
  'addendum.sheetSubtitle': '原报告保持不变',
  'addendum.languageHint': '客户拿到的是英文报告，补充说明请用英文写。',
  'addendum.item': '更正哪一条',
  'addendum.itemPlaceholder': '例如 Kitchen · Sink，第 6 页',
  'addendum.reason': '原因',
  'addendum.reasonPlaceholder': '例如 The wrong photo was attached',
  'addendum.correction': '更正内容',
  'addendum.correctionPlaceholder': '报告这一条应当怎么写',
  'addendum.photos': '照片（可选，最多 4 张）',
  'addendum.addPhotos': '从相册选',
  'addendum.removePhoto': '移除照片 {n}',
  'addendum.photoLimit': '最多 4 张照片，多出的没有加入',
  'addendum.signature': '验屋师签名',
  'addendum.clearSignature': '重签',
  'addendum.drawHere': '在这里签名',
  'addendum.required': '请填写这一项',
  'addendum.signRequired': '请先签名',
  'addendum.dateLine': '日期：{date}',
  'addendum.create': '生成补充说明 PDF',
  'addendum.creating': '正在生成…',
  'addendum.created': '补充说明 {n} 已生成，也会存到账户里',
  'addendum.failed': '补充说明没能生成，请再试一次。',
  'addendum.print.title': '{title}补充说明',
  'addendum.print.number': '第 {n} 号补充说明',
  'addendum.print.property': '房屋地址',
  'addendum.print.client': '客户',
  'addendum.print.original': '原报告',
  'addendum.print.originalLine': '{title}，出具于 {date}',
  'addendum.print.issued': '本补充说明日期',
  'addendum.print.item': '更正的条目',
  'addendum.print.reason': '原因',
  'addendum.print.correction': '更正内容',
  'addendum.print.photos': '照片',
  'addendum.print.photoCaption': '补充照片 {n}',
  'addendum.print.statement': '本补充说明是上述报告的一部分。原报告没有任何改动；除本补充说明写明的内容外，原报告保持出具时的样子。',
  'addendum.print.inspector': '验屋师',
  'addendum.print.signature': '签名',
  'addendum.print.notRecorded': '未记录',
  'report.agreementDocument': '协议文本',
  'report.agreementSigned': '由 {client}（客户）与 {inspector}（验屋师）于 {date} 签署',
  'report.agreementSignedLabel': '签署',
  'report.agreementNotSigned': '未签署',
  'report.agreementSeparate': '已签署的验屋协议单独导出为一份 PDF，不包含在报告内。',
  'report.agreementUnsignedNote': '在「验屋详情」中签署验屋协议后，可单独导出协议 PDF。',
  'report.demoBanner': 'DEMO —— 非付费验屋。本文件仅用于演示。',
  'report.demoWatermark': 'DEMO 演示',
  'report.feeDemo': '演示（无费用）',
  'agreement.documentTitle': '验屋协议',
  'agreement.viewSubtitle': '固定文本 · 纽约州',
  'agreement.fixedNote': '协议文本固定不变；每次验屋只填写房产、客户、验屋师、日期、费用与签名。',
  'agreement.languageLabel': '协议语言',
  'agreement.languageNote': '协议只出一种语言，由你给客户选：全中文版或全英文版。',
  'agreement.languageBilingualOld': '这份协议按旧版签署，是中英双语的。选一种语言会重新打开协议、按新版重签。',
  'agreement.feeLabel': '验屋费用（美元）',
  'agreement.feePlaceholder': '例：450',
  'agreement.orDemo': '或',
  'agreement.reopenTitle': '改这一项，协议要重新签',
  'agreement.reopenBody': '协议已经签好。改动后协议会变回「未签」，需要客户和验屋师重新签字。',
  'agreement.reopenConfirm': '改，之后重新签',
  'agreement.demo': '演示验屋（无费用）',
  'agreement.demoHint': '演示验屋照常记录；协议与报告都会带 DEMO 水印。',
  'agreement.clientSignature': '客户签名',
  'agreement.inspectorSignature': '验屋师签名',
  'agreement.notSigned': '未签署',
  'agreement.signed': '已签署',
  'agreement.signedOn': '{date} 签署',
  'agreement.missing': '尚缺：{list}',
  'agreement.req.fee': '费用或演示',
  'agreement.req.feeFirst': '签署前请先填写费用或选择演示。',
  'agreement.req.clientName': '客户姓名',
  'agreement.req.clientSignature': '客户签名',
  'agreement.req.clientAccepted': '客户确认',
  'agreement.req.inspectorName': '验屋师姓名',
  'agreement.req.inspectorSignature': '验屋师签名',
  'agreement.req.inspectorAccepted': '验屋师确认',
  'agreement.sign': '签署协议',
  'agreement.resign': '重新签署',
  'agreement.signTitle': '签署协议',
  'agreement.signBody': '双方在本机上签名；签名随验屋保存，并印在协议 PDF 上。',
  'agreement.readBeforeSigning': '先阅读协议',
  'agreement.signerName': '姓名',
  'agreement.signerNamePlaceholder': '签名者全名',
  'agreement.drawHere': '在此签名',
  'agreement.clear': '清除',
  'agreement.acceptClient': '我已阅读并同意本验屋协议',
  'agreement.acceptInspector': '我确认以上验屋信息与检查范围',
  'agreement.completeSigning': '完成签署',
  'agreement.pdfShare': '分享协议 PDF',
  'agreement.pdfShared': '协议 PDF 已生成',
  'agreement.pdfUnsigned': '请先签署协议再导出。',
  'agreement.pdfFailed': '协议 PDF 没能生成，请再试一次。',
  'agreement.docProperty': '房产',
  'agreement.docClient': '客户',
  'agreement.docInspector': '验屋师',
  'agreement.docInspectionDate': '验屋日期',
  'agreement.docFee': '费用',
  'agreement.docVersion': '文本版本',
  'agreement.feeDemo': '演示 —— 无费用',
  'agreement.clauseNote': '以下条款为纽约州法定要求，原文照录。',
  'agreement.sectionDetails': '验屋信息',
  'agreement.sectionScope': '检查范围',
  'agreement.sectionClauses': '法定条款',
  'agreement.sectionSignatures': '签名',
  'agreement.languageVersion': '中文版',
  'inspectionStage.draft': '草稿',
  'inspectionStage.inProgress': '进行中',
  'inspectionStage.ready': '待收尾',
  'inspectionStage.reportComplete': '报告已完成',
  'agreement.paymentOutside': '付款在 App 外完成 —— 这里没有发票或在线支付。',
  'agreement.signedAtLabel': '签署时间',
  'common.yes': "是",
  'common.no': "否",
  'ai.fail.blockedRealClient': "本次验屋暂不开放云端 AI 分析。现场验屋、照片、备注和检查功能仍可正常使用。",
  'ai.fail.blockedLegalReview': "该类型物业暂不开放 AI 分析。现场验屋、照片、备注和检查功能仍可正常使用。",
  'ai.fail.blockedJurisdiction': "填写执照信息并确认物业所在州后，才会开放 AI 分析。",
  'gate.title': "继续本次验屋前",
  'gate.viewOnly': "已记录的内容都会保留，可以随时查看，不会删除任何数据。",
  'gate.licenceTitle': '请先绑定执照',
  'gate.licenceBody': '执照和纽约州公开名单核对后绑定到账号，只需一次，之后每次新建验屋都会自动写入。',
  'gate.openProfile': "验屋师资料",
  'gate.stateTitle': "本物业位于哪个州？",
  'gate.stateBody': "请选择所在州，以便适用正确的验屋规则。本次验屋此前没有确认过。",
  'gate.unsupportedTitle': "该州暂未开放",
  'gate.unsupportedBody': "{state} 尚未开放完整验屋支持，本物业不能使用正式验屋流程。",
  'gate.mismatchTitle': "执照与物业不在同一个州",
  'gate.mismatchBody': "您的执照登记在 {inspectorState}，本物业位于 {propertyState}。该组合暂未开放正式验屋流程。",
  'gate.occupancyTitle': "本物业此前是否曾作为住宅入住？",
  'gate.occupancyBody': "本次验屋只需回答一次。请根据您对该物业的了解作答。",
  // （新建屋阻断文案已于 2026-09-09 撤下：OD-9 改为按「新建屋目视观察」提供服务，不再阻断。
  //   户数超限的阻断有自己的文案。）
  'gate.actionUnavailable': "本次验屋尚未开放正式流程。",
  'settings.profile': "验屋师资料",
  'settings.profileBody': '显示名、照片、执照绑定',
  'settings.report': '报告样式与品牌',
  'settings.reportBody': '公司名、logo、联系方式、缺陷级别名称',
  'branding.companyTitle': '公司与联系方式',
  'branding.companyBody': '印在报告封面和每一页的页眉；不填的项目不印。',
  'branding.noLogo': '未设 logo',
  'branding.logoPick': '选择 logo',
  'branding.logoChange': '更换 logo',
  'branding.logoRemove': '移除',
  'branding.logoFailed': 'logo 保存失败：{message}',
  'branding.companyName': '公司名称',
  'branding.companyNamePlaceholder': '例：长岛验屋服务有限公司',
  'branding.phone': '电话',
  'branding.email': '邮箱',
  'branding.website': '网站',
  'branding.websitePlaceholder': '例：www.example.com',
  'branding.showInspectorPhoto': '在报告里印我的头像',
  'branding.showInspectorPhotoHint': '头像在「验屋师资料」里设置，印在报告的验屋信息旁。',
  'branding.tiersTitle': '缺陷级别名称',
  'branding.tiersBody': '三个级别的颜色固定（红 · 橙 · 蓝）。名称可以改成你习惯的说法，留空就用标准名称。英文报告用英文名称，中文自留版用中文名称。',
  'branding.tierNamesEnglish': '英文报告',
  'branding.tierNamesChinese': '中文自留版',
  'branding.saved': '已保存，下次生成报告时生效。',
  'profile.body': '显示名和照片是你自己设的；执照和纽约州名单核对后绑定到账号，每份新验屋自动写入，之后改动不会影响已经写入的验屋。',
  'profile.name': '显示名',
  'profile.licensedState': "执照所在州",
  'profile.licenseNumber': "执照号码",
  'licence.number': '执照号',
  'licence.numberPlaceholder': '11 位数字，例如 16000012345',
  'licence.holder': '持证人',
  'licence.expires': '有效期至',
  'licence.firstName': '名（按执照上的写法）',
  'licence.lastName': '姓（按执照上的写法）',
  'licence.bindBody': '填执照上的号码和名字，我们会和纽约州公开的持证验屋师名单核对。核对无误后，执照号和持证人名字会自动印在每份报告上，之后不能自行修改。',
  'licence.bind': '核对并绑定',
  'licence.binding': '正在核对…',
  'licence.notListed': '纽约州名单里查不到这个执照号，请对照执照再填一次。',
  'licence.expiredOnList': '纽约州名单显示这个执照已过期，过期执照不能绑定。',
  'licence.taken': '这个执照号已经绑定在另一个账号上。一个执照号只能绑一个账号；如有疑问请联系客服。',
  'licence.accountHasAnother': '这个账号已经绑定了另一个执照号。换执照请联系客服。',
  'licence.nameMismatch': '名字和纽约州名单对不上：姓要完全一样，名的第一个字母要一样。请按执照上的英文写法填。',
  'licence.listUnavailable': '暂时查不到纽约州的名单，请稍后再试。',
  'licence.offline': '现在离线，联网后才能核对执照。',
  'licence.unavailable': '暂时连不上服务器，请稍后再试。',
  'licence.deviceNotVerified': '这台设备还没完成邮箱验证，验证后才能绑定执照。',
  'licence.lockedNote': '执照已与账号绑定，不能自行修改。换执照请发邮件到 support@nezha2capital.com（标题加 [验屋师]）并附执照证明。',
  'licence.expiredNote': '执照已过期，不能出正式报告；演示验屋不受影响。续期后点下面重新核对。',
  'licence.recheck': '重新核对',
  'licence.displayNameNote': '这是 App 里显示的名字。报告上印的是执照上的持证人名字。',
  'report.licenceMissing': '正式报告要先绑定执照：纽约州法律要求每份报告印执照号。',
  'report.licenceExpired': '执照已过期，不能出正式报告。续期后到「验屋师资料」重新核对。',
  'device.title': '新设备验证',
  'device.body': '这台设备第一次登录这个账号。为防止账号借给别人用，需要验证一下：点下面的按钮，我们会向 {email} 发 6 位验证码，填进来就能继续。',
  'device.bodyNoEmail': '这台设备第一次登录这个账号，需要邮箱验证码才能继续。',
  'device.codeLabel': '验证码',
  'device.send': '发送验证码',
  'device.resend': '重新发送',
  'device.sent': '验证码已发送，请查收邮件（也看看垃圾邮件）。',
  'device.rateLimited': '发送太频繁了，请等一分钟再试。',
  'device.sendFailed': '验证码没发出去，请稍后再试。',
  'device.verify': '验证',
  'device.invalid': '验证码不对或已过期，请重新输入或重新发送。',
  'device.verifyFailed': '暂时验证不了，请稍后再试。',
  'device.limitNote': '一个账号同时最多在 2 台设备上登录（比如手机和电脑）。验证这台以后，最久没用的那台会自动退出。',
  'device.signOut': '退出登录',
  'device.revoked': '你的账号在另一台设备登录了（同时最多 2 台），这台设备已退出。重新登录需要邮箱验证码。',
  'device.listTitle': '已登录的设备（最多 2 台）',
  'device.thisDevice': '这台设备',
  'device.listNote': '一个账号同时最多在 2 台设备上登录；在第 3 台登录时，最久没用的那台会自动退出。',
  'profile.licenseNumberHint': "请按执照上印刷的内容填写。",
  'profile.selectState': "请选择州",
  'profile.licenseShape': "请按执照上的格式填写执照号码。",
  'profile.status': "执照状态",
  'profile.statusLicensed': '已绑定，有效',
  'profile.statusMissing': '未绑定或已过期',
  'profile.saved': "验屋师资料已保存",
  'setup.statePlaceholder': "选择",
  'setup.needState': "请选择物业所在的州。",
  'setup.needOccupancy': "请回答本物业此前是否曾入住。",
  'setup.needLicence': '请先绑定执照。',
  'setup.fillLicence': "填写执照",
  'setup.stillMissing': "还不能创建，缺少：{list}",
  'setup.missingAddress': "物业地址",
  'setup.missingLayout': "房屋布局",
  'setup.missingStart': "起步方式",
  'setup.stateNotOpen': "{state} 尚未开放完整验屋支持。",
  // ---- NY F1 Round 2 · 法定描述项（OD-1 = A1；NY-REPORT-AUTHORITY §8 / §9）----
  'descriptor.roofMaterialCondition': '屋面材料与状况',
  'descriptor.roofObservationMethod': '屋面观察方式',
  'descriptor.waterSupplyPipingMaterial': '供水管材',
  'descriptor.dwvPipingMaterial': '排水 / 通气管材',
  'descriptor.waterHeaterCapacity': '热水器容量',
  'descriptor.waterHeaterEnergySource': '热水器能源',
  'descriptor.mainWaterShutoffLocation': '总水阀位置',
  'descriptor.mainFuelShutoffLocation': '总燃料关断阀位置',
  'descriptor.heatingFuel': '供暖燃料',
  'descriptor.waterSupplySource': '供水来源',
  'descriptor.wasteDisposalSource': '排污去向',
  'descriptor.serviceAmperageVoltage': '供电容量（安培 / 电压）',
  'descriptor.disconnectLocations': '总开关与分箱位置',
  'descriptor.aluminumBranchWiring': '铝质分支布线',
  'descriptor.smokeDetectorPresence': '烟雾报警器',
  'descriptor.coDetectorPresence': '一氧化碳报警器',
  'descriptor.branchConductorCondition': '可见分支导线',
  'descriptor.heatingEquipmentType': '供暖设备',
  'descriptor.heatingDistribution': '供暖分配方式',
  'descriptor.heatSourceEachHabitableSpace': '各居住空间热源',
  'descriptor.coolingEquipmentType': '制冷设备',
  'descriptor.coolingDistribution': '制冷分配方式',
  'descriptor.interiorSurfaces': '墙、顶、地',
  'descriptor.insulationUnfinishedSpaces': '未装修空间保温',
  'descriptor.ventilationAtticFoundation': '阁楼与地基区域通风',
  'descriptor.atticObservationMethod': '阁楼观察方式',
  'descriptor.atticConditionsObserved': '阁楼观察到的状况',
  'descriptorChoice.roofObservationMethod.walked': '上屋顶',
  'descriptorChoice.roofObservationMethod.ladderAtEave': '梯子在屋檐边',
  'descriptorChoice.roofObservationMethod.groundZoom': '地面变焦',
  'descriptorChoice.roofObservationMethod.drone': '无人机',
  'descriptorChoice.roofObservationMethod.notObserved': '未观察',
  'descriptorChoice.waterHeaterEnergySource.gas': '燃气',
  'descriptorChoice.waterHeaterEnergySource.oil': '燃油',
  'descriptorChoice.waterHeaterEnergySource.electric': '电',
  'descriptorChoice.waterHeaterEnergySource.propane': '丙烷',
  'descriptorChoice.waterHeaterEnergySource.other': '其他',
  'descriptorChoice.heatingFuel.gas': '燃气',
  'descriptorChoice.heatingFuel.oil': '燃油',
  'descriptorChoice.heatingFuel.electric': '电',
  'descriptorChoice.heatingFuel.propane': '丙烷',
  'descriptorChoice.heatingFuel.other': '其他',
  'descriptorChoice.heatingFuel.unknown': '不确定',
  'descriptorChoice.waterSupplySource.public': '市政',
  'descriptorChoice.waterSupplySource.private': '自有（井水）',
  'descriptorChoice.waterSupplySource.unknown': '不确定',
  'descriptorChoice.wasteDisposalSource.public': '市政',
  'descriptorChoice.wasteDisposalSource.private': '自有（化粪池）',
  'descriptorChoice.wasteDisposalSource.unknown': '不确定',
  'descriptorChoice.aluminumBranchWiring.present': '有',
  'descriptorChoice.aluminumBranchWiring.notObserved': '未观察到',
  'descriptorChoice.smokeDetectorPresence.present': '有',
  'descriptorChoice.smokeDetectorPresence.absent': '无',
  'descriptorChoice.smokeDetectorPresence.partial': '部分',
  'descriptorChoice.coDetectorPresence.present': '有',
  'descriptorChoice.coDetectorPresence.absent': '无',
  'descriptorChoice.coDetectorPresence.partial': '部分',
  'descriptorChoice.heatingDistribution.forcedAir': '风管送风',
  'descriptorChoice.heatingDistribution.hydronic': '热水（水暖）',
  'descriptorChoice.heatingDistribution.steam': '蒸汽',
  'descriptorChoice.heatingDistribution.radiant': '辐射',
  'descriptorChoice.heatingDistribution.baseboard': '电踢脚线',
  'descriptorChoice.heatingDistribution.other': '其他',
  'descriptorChoice.heatSourceEachHabitableSpace.allSpaces': '所有居住空间',
  'descriptorChoice.heatSourceEachHabitableSpace.someSpaces': '仅部分空间',
  'descriptorChoice.heatSourceEachHabitableSpace.none': '无',
  'descriptorChoice.coolingDistribution.ducted': '风管',
  'descriptorChoice.coolingDistribution.ductless': '分体无风管',
  'descriptorChoice.coolingDistribution.roomUnits': '房间机',
  'descriptorChoice.coolingDistribution.other': '其他',
  'descriptorChoice.atticObservationMethod.entered': '进入阁楼',
  'descriptorChoice.atticObservationMethod.fromHatch': '仅从入口观察',
  'descriptorChoice.atticObservationMethod.notObserved': '未观察',
  'descriptor.sectionTitle': '报告描述项',
  'descriptor.sectionHint': '即使一切正常也会印在报告里。每项点一下即可。',
  'descriptor.needed': '最终报告需要，尚未记录。',
  'descriptor.textPlaceholder': '一句话',
  'descriptor.detailPlaceholder': '位置 / 哪些房间（可选）',
  'descriptor.prefilled': '已按供暖燃料预填，热水器不同请改。',
  'descriptor.safetyPrompt': '缺少报警器属安全隐患，请记录一条安全问题。',
  'descriptor.recordSafetyFinding': '记录安全问题',
  'descriptor.safetyFindingTitle': '缺少烟雾 / 一氧化碳报警器',
  'facts.title': '房屋事实',
  'facts.hint': '只问一次，印在报告里。',
  'readiness.title': '报告必填描述项',
  'report.statusCloseoutClear': '收尾检查没有待补项；还要补齐下面的描述项才能生成最终报告。',
  'readiness.ready': '所有必填描述项已记录，可以生成最终报告。',
  'readiness.blocked.one': '还差 {n} 项描述项才能生成最终报告',
  'readiness.blocked.other': '还差 {n} 项描述项才能生成最终报告',
  'readiness.propertyFact': '房屋事实',
  'readiness.fix': '去记录',
  'readiness.licenceWarning': '尚未记录验屋师执照号，报告将不印执照号。',
  'readiness.notApplicable': '演示验屋不要求报告描述项。',
  'report.description': '描述信息',
  'report.observedComponents': '已观察部件',
  'report.observed': '已观察',
  'report.observedDefect': '已观察 — 缺陷',
  'report.notObserved': '未观察 →',
  'report.notPresentSection': '现场未安装 / 无此项',
  'report.notPresentWording': '现场未安装 / 无此项',
  'report.licensedInspector': '纽约州持证住宅验屋师',
  'report.licenseNumber': '执照号',
  'report.waterSupply': '供水来源',
  'report.wasteDisposal': '排污去向',
  'report.heatingFuel': '供暖燃料',

  // ---- NY F1 Round 3: 未观察部位（OD-5 = A）· 无法检查原因（OD-7 = C）· 车棚（AG-01 product half）----
  'unable.scopeWhole': '整项无法检查',
  'unable.scopePartial': '部分未能观察',
  'unable.portionTitle': '部分未观察',
  'unable.partialBody': '选择这一部分无法观察的原因；已观察部分的结果保留不变。',
  'unable.partialNeedsResult': '已观察部分仍需单独给出结果（无异常 / 缺陷）。',
  'unable.extent': '未观察的部位',
  'unable.extentPlaceholder': '如：太阳能板下方的南坡 · 堆放物品后的北墙',
  'unable.reasonWords': '请写明实际原因',
  'unable.otherNeedsReason': '「其他」必须写明实际原因。',
  'unable.reportReason': '报告将写明',
  'unable.obstruction': '遮挡物',
  'unable.obstructionGeneric': '存放物品、覆盖物或装修饰面（通用措辞）',
  'unable.obstructionSolar': '太阳能板 / 太阳能阵列',
  'unable.partialUnavailable': '「部分未能观察」需要已观察部分先有结果（无异常 / 缺陷）。这一项目前是整项无法检查或不适用。',
  'unable.confirmPortion': '记录未观察部位',
  'unable.replacesPortions.one': '将替换已记录的 {n} 处未观察部位——整项记为未观察。',
  'unable.replacesPortions.other': '将替换已记录的 {n} 处未观察部位——整项记为未观察。',
  'item.unableNeedsNote': '原因为「其他」时必须写明实际原因。',
  'item.limitedPortions': '未观察部位',
  'item.limitedPortionsHint': '已观察部分保留结果；这里记录的每一处都会作为限制说明打印并注明原因。',
  'item.addLimitedPortion': '记录未观察部位',
  'item.portionRemoveTitle': '移除已记录的未观察部位？',
  'item.portionRemoveBody.one': '这一项已记录 {n} 处未观察部位。整项改为{result}将移除这些记录。是否继续？',
  'item.portionRemoveBody.other': '这一项已记录 {n} 处未观察部位。整项改为{result}将移除这些记录。是否继续？',
  'item.portionRemoveConfirm': '移除并改为{result}',
  'item.portionUnnamed': '未指明部位',
  'item.portionEdit': '修改',
  'item.portionRemove': '移除',
  'item.portionNeedsNote': '原因为「其他」的未观察部位必须写明实际原因。',
  'area.limitedPortionsMeta.one': '{n} 处未观察',
  'area.limitedPortionsMeta.other': '{n} 处未观察',
  'closeout.gapUnableNoNote': '无法检查（其他）缺原因说明',
  'closeout.gapPortionNoNote': '未观察部位（其他）缺原因说明',
  'report.portion': '未观察部位',
  'report.portionUnspecified': '该部件的一部分',
  'report.observedPartial': '部分未观察 →',
  'report.supplementalRow': '补充观察（超出纽约最低标准）',
  'space.carport': '车棚',
  'layout.garageCarport': '车棚',

  // ---- NY F1 Round 5 · Owner rulings 2026-09-09（OD-8 … OD-13）+ Owner default X-1（2026-09-11）----
  // 报告流程文案 = 批准草案 NY-AGREEMENT-V2-AND-REPORT-FLOW-APPROVED-DRAFT-20260909 §5.1 / §5.2 /
  // §5.3 与隐私段落 §6 的逐字文本；门禁、户数、同意与自愿观察文案为本轮新写，待创始人复核。
  'service.homeInspection': '验屋',
  'service.newBuild': '新建屋',
  'gate.agreementTitle': '请先签署验屋协议',
  'gate.agreementBody': '客户与你签署本次验屋的协议后才能开始检查。已记录的内容全部保留。',
  'gate.openAgreement': '打开协议',
  'gate.unitCountTitle': '超过 4 户的物业暂不支持',
  'gate.unitCountBody': '正式验屋流程只适用于 1 至 4 户的住宅建筑。如户数记录有误，请在下方更正。',
  'setup.unitCount': '户数',
  'setup.unitCountHint': '该建筑的住宅户数。',
  'setup.unitCountOverLimit': '5 户及以上',
  'setup.unitCountBlocked': '正式验屋流程不适用于超过 4 户的建筑。',
  'setup.agreementLater': '可以先创建验屋、稍后签署协议；双方签署后才能开始检查。',
  'setup.countTrialTitle': '这次会用掉 1 次免费验屋',
  'setup.countSingleTitle': '这次会用掉 1 次单次购买',
  'setup.countBody': '一旦创建，这次就算用掉了。如果这一单没出报告、也没用过 AI 就删除，用掉的这次会退回；「删除退回」另有限制：每 30 天最多退回 {limit} 次。用过 AI 或已出报告的不退回。请确认现在可以正常完成这次验屋。',
  'setup.countRemaining': '用掉后还剩 {n} 次。',
  'setup.countTrialTotal': '每个账号一共 {n} 次免费验屋，不会按月重新给。',
  'setup.missingState': '物业所在州',
  'setup.missingOccupancy': '是否曾入住',
  'setup.clientPhonePlaceholder': '例：516-555-0123',
  'setup.clientEmailPlaceholder': '例：name@example.com',
  'setup.inspectorFromLicence': '验屋师姓名取自账号绑定的执照，协议和报告上印的就是这个名字。',
  'setup.countConfirm': '确认，开始验屋',
  'setup.newBuildHint': '新建或从未入住的房屋请选「否」：按新建屋目视观察办理，使用单独的协议与报告。',
  'agreement.aiConsentTitle': 'AI 辅助分析与云端存储',
  'agreement.aiConsentToggle': '客户同意（可选）',
  'agreement.aiConsentOptional': '可选，由客户在签署时自己勾选。不同意时验屋与报告完全相同，只是不使用 AI 辅助分析，报告也只保存在本设备上。',
  'agreement.aiConsentBySigning': '签署时由客户勾选',
  'agreement.aiConsentRowLabel': 'AI 与云端存储',
  'agreement.aiConsentGivenOn': '客户已于 {date} 勾选同意',
  'agreement.esignTick': '我同意以电子方式签署本协议，并以电子方式收取本协议与报告（随时可改要纸质版）',
  'agreement.aiTick': '我同意照片用于 AI 辅助分析，最终报告保存在验屋师的云端账户',
  'agreement.consentsNote': '每一项都由客户本人勾选。完整条款见上方「先阅读协议」。',
  'agreement.liabilityTick': '我已阅读并同意「责任限制与索赔」条款（赔偿以验屋费为限、修理前先通知、一年内起诉、报告仅供本人使用）',
  // 发链接远程签（Owner 待决清单 2026-10-19 第 4、45 项）。
  'agreement.signedRemotely': '经一次性链接远程签署；服务器于 {date} 收到，来自 IP 地址 {ip}。',
  'agreement.signedRemotelyNoIp': '经一次性链接远程签署；服务器于 {date} 收到。',
  'remote.title': '发链接远程签',
  'remote.subtitle': '客户不在现场时用：客户在自己的手机或电脑上签。',
  'remote.step1': '把链接发给客户。客户打开后不用注册，读完整协议，勾选同意，写名字并手签。',
  'remote.step2': '回到这里点「检查客户是否已签」，客户的签名会进到这份协议里，我们服务器上的那份随即删除。',
  'remote.step3': '然后你在这台设备上签名，协议就完成了。链接 14 天内有效，只能签一次。',
  'remote.changeNote': '先填好费用（或打开演示）和附加服务。发出链接后如果改了费用、附加服务、日期、地址或姓名，客户的签名就对不上了，要重新发链接。',
  'remote.notReady': '发链接前请先填好费用或打开演示，并把附加服务填完整。',
  'remote.noWebAddress': '这个版本不知道网页版的地址，没法生成链接。',
  'remote.offline': '没有网络，连上网再试。',
  'remote.notSignedIn': '请重新登录后再试。',
  'remote.unavailable': '远程签暂时用不了，请稍后再试，或改为当面签。',
  'remote.waiting': '客户还没签。',
  'remote.taken': '客户已签好。现在请你签名，完成协议。',
  'remote.expired': '这个链接已过期，请重新生成。',
  'remote.changed': '发出链接后协议内容改过（费用、附加服务、日期、地址或姓名），客户签的是旧版本，这个签名没有采用，链接也已作废。请重新生成链接。',
  'remote.gone': '这个链接已经不在了，请重新生成。',
  'remote.copied': '链接已复制。',
  'remote.copyByHand': '这里不能自动复制，请选中上面的链接手动复制。',
  'remote.shareText': '请阅读并签署 {property} 的验屋协议：',
  'remote.validUntil': '{date} 前有效。',
  'remote.copy': '复制链接',
  'remote.share': '分享',
  'remote.check': '检查客户是否已签',
  'remote.checking': '正在检查…',
  'remote.discard': '作废这个链接',
  'remote.create': '生成链接',
  'remote.creating': '正在生成链接…',
  'remote.signNow': '现在签名',
  'remote.cardWaiting': '已发远程签链接，等待客户签署（{date} 前有效）。',
  'remote.cardClientSigned': '客户已远程签署，你签名后协议即完成。',
  'remote.openLink': '远程签链接',
  'remote.sendLink': '发链接给客户远程签',
  'remote.inPersonInstead': '改为当面签',
  'remote.signedAt': '远程签署，收到时间 {date}',
  'remote.esignGiven': '已于 {date} 勾选电子记录与电子签名同意',
  'remote.liabilityGiven': '已于 {date} 勾选同意责任限制与索赔条款',
  'remote.liabilityMissing': '没有勾选责任限制与索赔条款：这份要当面重签',
  'remote.aiGiven': '已于 {date} 勾选云端存储与 AI 辅助分析同意',
  'remote.aiNotGiven': '没有勾选云端存储与 AI 辅助分析同意',
  'remotePage.title': '验屋协议',
  'remotePage.intro': '你的验屋师把这份协议发给你签署。请先读完下面的全文，再在最下面勾选、写名字并签名。',
  'remotePage.documentTitle': '协议全文',
  'remotePage.consentsNote': '每一项都由你本人勾选，完整条款在上面的协议全文里。',
  'remotePage.needEsign': '要在这里签，需要勾选电子记录与电子签名同意（也可以向验屋师要纸质版）。',
  'remotePage.needLiability': '要签这份协议，需要勾选同意责任限制与索赔条款。',
  'remotePage.needName': '请写上你的名字。',
  'remotePage.needSignature': '请用手指或鼠标在框里签名。',
  'remotePage.needAccept': '请勾选已阅读并同意。',
  'remotePage.submit': '签署协议',
  'remotePage.sending': '正在提交…',
  'remotePage.done': '已签署，谢谢。',
  'remotePage.doneNext': '接下来验屋师签名，然后把双方签好的协议 PDF 发给你。你可以关掉这个页面了。',
  'remotePage.alreadySigned': '这份协议已经签过了，验屋师会把签好的副本发给你。',
  'remotePage.expired': '这个链接已过期，请向验屋师要一个新链接。',
  'remotePage.notFound': '这个链接无效，请向验屋师要一个新链接。',
  'remotePage.unavailable': '暂时打不开这份协议，请稍后再试。',
  'remotePage.offline': '没有网络，请检查网络后再点签署。',
  'remotePage.privacy': '不需要注册账户。你的名字、签名和选择只交给你的验屋师；验屋师取回后，我们服务器上的那份即删除。',
  'agreement.tickedAt': '客户勾选时间：{date}',
  'agreement.req.clientEsignConsent': '客户电子签署同意',
  'agreement.req.clientLiabilityAck': '客户同意责任限制条款',
  'agreement.req.additionalServices': '附加服务的名称与费用',
  'agreement.req.servicesFirst': '签署前请先补全附加服务的名称与费用。',
  'agreement.serviceName': '附加服务',
  'agreement.serviceNamePlaceholder': '例：独立车库以外的棚屋',
  'agreement.serviceFee': '费用（美元）',
  'agreement.serviceAdd': '添加附加服务',
  'agreement.serviceRemove': '删除',
  'agreement.servicesNone': '无附加服务',
  'agreement.aiConsentGiven': '已同意',
  'agreement.aiConsentNone': '未同意',
  'agreement.aiConsentWithdraw': '记录书面撤回',
  'agreement.consentWithdrawnOn': '客户已于 {date} 书面撤回同意',
  'ai.fail.blockedAgreementUnsigned': '签署验屋协议后才能使用 AI 分析。现场验屋、照片、备注和检查功能仍可正常使用。',
  'ai.fail.blockedNotConsented': '客户未同意，或已撤回本次验屋的 AI 辅助分析同意。其他功能仍可正常使用。',
  'ai.fail.blockedAgreementOutdated': '本次验屋签署的是不含 AI 同意条款的旧版协议。请客户签署当前版本协议后再使用 AI 分析。',
  'report.previewed': '已预览 ✓',
  'report.agreementPrepared': '签署后已生成 ✓ 不用预览',
  'report.agreementNoPreview': '不用预览',
  'report.agreementDrawing': '正在生成 PDF',
  'report.agreementFailed': '签署后没能生成 PDF',
  'agreement.pdfKeptFailed': '协议 PDF 没能生成。点「重试」再生成一次；生成最终报告时也会再生成一次。',
  'report.outputGenerated': '已生成 ✓',
  'report.previewFirst':'生成前请先预览每一份报告。最终 PDF 生成后无法更改。',
  'report.previewPending.one': '还有 {n} 份报告未预览',
  'report.previewPending.other': '还有 {n} 份报告未预览',
  'report.finalConfirmTitle': '生成最终报告？此操作无法撤销。',
  'report.finalConfirmBody': '生成后本次验屋即为最终状态：任何结果、问题记录、照片、备注或限制说明都无法再编辑、新增或删除；如需更正，只能另出补充说明。本次验屋的全部工作数据——应用内的照片、备注、问题记录与 AI 建议——将从本设备和我们的服务器上删除，只保留最终 PDF 报告与已签署的协议 PDF。客户同意云端存储时，这些 PDF 也保存在你的账户里。用相机拍摄的照片仍保留在设备相册中。报告须在验屋完成后五个工作日内交付客户。',
  'report.finalConfirmCheck': '我已预览报告，并了解生成后无法更改。',
  'report.finalConfirmAction': '生成并删除工作数据',
  'report.purgedBanner': '最终报告已生成。工作数据已删除，仅保留 PDF 文件。',
  'reports.generated': '已出报告',
  'report.otherObservations': '其他观察（纽约验屋标准规范不要求）',
  'finding.voluntaryQuick': '已看过 · 未见问题',
  'finding.voluntaryTag': 'NY 标准不要求 · 验屋师自行观察',
  'finding.voluntaryHint': '作为附带观察记录：印在「其他观察」之下，不属于本次验屋范围。',
  'finding.voluntarySaved': '已记为自愿观察',
  'finding.voluntaryTitlePlaceholder': '你看的是什么（例：草坪喷灌）',
  'reportDue.newBuild': '报告应于 {date} 前交付——协议约定在观察完成后五个工作日内交付。',
  'reportDue.newBuildOverdue': '报告已于 {date} 到期——协议约定在观察完成后五个工作日内交付。',
  'inspections.deletePurgedBody': '这条验屋只剩下 PDF 文件：最终报告与已签署的协议。删除会从本机移除这些文件，且无法撤销。',
  'settings.privacyNotice': '你的验屋记录保存在本设备上。只有当你点击 AI 分析时，照片、备注与清单上下文才会发送至我们的后端（Supabase，位于美国的 AWS），并仅用于转发给 Anthropic 分析；后端不存储照片，日志中也没有照片。Anthropic 不会用你的内容训练模型，通常情况下保留不超过 30 天。不会发送客户姓名或地址。生成最终报告会从应用中删除该验屋的工作数据，仅保留 PDF。你的登录邮箱与密码受行业标准哈希与传输加密保护；你可随时在设置中删除账号。',
  // ---- 高级 AI 复核 · AI 任务 · 通知（Owner 合同 2026-09-13；§56 冻结文案）----
  'ai.disclaimer': 'AI也可能出错，所有分析结果仅供参考。',
  'advanced.title': '高级 AI 深度复核',
  'advanced.start': '开始深度复核',
  'advanced.starting': '正在开始…',
  'advanced.processing': '高级 AI 分析中 · 可继续验屋',
  'advanced.completeLine': '高级 AI 复核完成',
  'advanced.completeToast': '高级 AI 复核完成 · {subject}',
  'advanced.viewResult': '查看结果',
  'advanced.busyHint': 'AI 服务暂时繁忙，系统会自动重试。你可以继续验屋。',
  'advanced.status.pendingUpload': '等待上传',
  'advanced.status.submitting': '正在提交',
  'advanced.status.notSubmitted': '未能提交',
  'advanced.status.queued': '已排队',
  'advanced.status.processing': '分析中',
  'advanced.status.retrying': '正在恢复',
  'advanced.status.completed': '已完成',
  'advanced.status.inputInvalid': '当前内容无法分析',
  'advanced.status.providerUnavailable': 'AI 服务暂时不可用',
  'advanced.status.failedFinal': '分析未完成',
  'advanced.status.interrupted': '已中断 · 正在恢复',
  'aiTasks.title': 'AI 任务',
  'aiTasks.subtitleAll': '本设备上的高级 AI 复核',
  'aiTasks.group.active': '分析中',
  'aiTasks.group.unread': '已完成未查看',
  'aiTasks.group.viewed': '已查看',
  'aiTasks.group.failed': '失败 · 需要处理',
  'aiTasks.counts': '{active} 分析中 · {unread} 未查看',
  'push.explainTitle': '允许通知',
  'push.explainBody': '允许通知后，高级 AI 分析完成时即使你正在检查其他区域也能收到提醒。',
  'push.allow': '允许通知',
  'push.later': '暂不',
  'setup.fixBeforeCreate': '请先补齐：{list}',
  'setup.fieldAddressRequired': '请填写房屋地址',
  'setup.fieldLayoutRequired': '请至少添加一个检查区域',
  'setup.fieldStartRequired': '请选择起步方式',
  'item.blockGuidance': '⑥ 检查要点',
  'item.blockPhotos': '③ 关联照片',
  'item.blockAi': '④ AI观察与可能的问题',
  'item.blockResult': '① 检查结果',
  'item.blockPortions': '⑤ 未观察部位',
  'item.blockWording': '② AI报告语',
  'item.guidanceNone': '这一项没有额外的检查要点。',
  'ai.itemHasObservation': 'AI 有观察',
  'ai.observation': 'AI观察',
  'ai.toVerify': 'AI待核实',
  'ai.noneForRow': '这一项暂无 AI 观察。',
  'ai.evidenceNote': 'AI 观察只是辅助证据，检查结果由你决定。',
  'ai.confidence.high': '置信度：高',
  'ai.confidence.medium': '置信度：中',
  'ai.confidence.low': '置信度：低',
  'ai.verdict.noVisibleDefect': 'AI 没发现 ≠ 无异常，请自行确认。',
  'ai.verdict.fieldTestRequired': '需现场测试，照片无法判断；请操作后再选结果。',
  'ai.suggestedEvidenceTitle': 'AI 放的照片',
  'ai.suggestedEvidenceHint': '已算这一项的证据、会进报告。放错了的点「不是这项」。',
  'ai.confirmEvidence': '确认',
  'ai.dismissEvidence': '不是这项',
  'ai.evidenceConfirmedToast': '已加入关联照片',
  'ai.suggestedEvidenceCount.one': 'AI 放了 {n} 张照片',
  'ai.suggestedEvidenceCount.other': 'AI 放了 {n} 张照片',
  'pool.pendingLink': 'AI 放的 · {target}',
  'ai.clearDefectTitle': '确定改成无异常？',
  'ai.clearDefectBody': 'AI 在这一项看到了「{headline}」。改成无异常后，报告只写无缺陷，这条 AI 观察不会进报告。',
  'ai.clearDefectConfirm': '改成无异常',
  'ai.unclassifiedTitle': 'AI发现 {n} 项未归类',
  'ai.unclassifiedHint': 'AI 未能对应到现有检查项的内容。归入检查项、记为其他问题，或忽略。',
  'ai.fileToItem': '归入现有检查项',
  'ai.filePickTitle': '归入哪一项？',
  'ai.filedToast': '已归入「{name}」',
  'ai.notFilable': '这条观察不在纽约验屋范围内，不能归入检查项；可记为其他问题，或忽略。',
  'ai.recordAsOther': '记为其他问题',
  'ai.needsOnline': '需要联网',
  'area.aiOffline': '需要联网',
  'area.aiStatusLine': '已分析 {photos} 张 · {rows} 项有 AI 观察 · {unclassified} 项未归类',
  'aiScan.help2': '点「AI 分析」后，照片、照片备注和区域整体备注会发给 AI；AI 把看到的内容对应到检查清单，作为每一项的 AI 观察。它不会给出结论：检查结果由你在每一项里选择。',
  'advanced.entry': '让高级 AI 再看',
  'advanced.explainer': '深度分析可能需要更长时间，并可能消耗更多 AI 使用额度。分析期间可以继续检查其他区域，任务可在后台运行。',
  'advanced.inputGuidance': '检查要点',
  'advanced.inputObservations': '普通 AI 观察',
  'advanced.inputNote': '验屋师备注',
  'advanced.deeper': '进行更深入复核。',
  'advanced.adjustPhotos': '查看 / 调整照片',
  'advanced.photosLabel': '要复核的照片',
  'advanced.noPhotos': '请先在③关联照片：高级 AI 只分析已关联到这一项的照片。',
  'advanced.resultLabel': '高级 AI 复核',
  'advanced.reviewedAt': '复核完成 · {time}',
  'advanced.confirmed': '可确认',
  'advanced.possible': '可能是什么',
  'advanced.unconfirmed': '无法仅凭照片确认',
  'advanced.onSite': '建议现场',
  'advanced.morePhotos': '需要补拍',
  'advanced.chatEntry': '继续问高级 AI',
  'advanced.again': '再次深度复核',
  'advanced.offlineHint': '需要联网',
  'chat.title': '高级 AI 对话',
  'chat.subtitle': '仅针对当前检查项',
  'chat.placeholder': '例如：这种情况现场还应该检查什么？',
  'chat.send': '发送',
  'chat.usageHint': '追问会继续消耗 AI 使用额度。',
  'chat.notRecord': 'AI 对话不是正式记录；只有加入验屋师备注的内容才成为报告事实。',
  'chat.answering': '高级 AI 正在回答…',
  'chat.addToNote': '加入验屋师备注',
  'chat.addToNoteHint': '可先修改，保存后进入验屋师备注。',
  'chat.addedToast': '已加入验屋师备注',
  'chat.you': '你',
  'chat.ai': '高级 AI',
  'chat.failed': '这一问未能送出：{reason}',
  'wording.en': '正式英文报告语（客户看到的）',
  'wording.zh': '中文参考译文',
  'wording.zhEditable': '中文报告语',
  'wording.zhStale': '中文还没按你改的英文同步（只影响中文自留版）。点下面的「AI 按我写的重新生成」即可同步。',
  'wording.enStale': '英文还没按你改的中文同步——客户版报告用的是英文。请点下面的「AI 按我写的重新生成」，或直接改英文；同步前不能生成最终报告。',
  'wording.trade': '建议处理专业人员',
  'wording.tradeNone': '（无）',
  'wording.write': 'AI写这一项',
  'wording.rewrite': 'AI重写',
  'wording.writing': 'AI 正在写…',
  'wording.regenerateFromMine': 'AI 按我写的重新生成',
  'wording.regenerateHint': 'AI 会保留你写的事实和意思，改成规范的报告用语，并让中英文一致。',
  'wording.regenerateTitle': '按你写的内容，让 AI 重新生成报告语？',
  'wording.regenerateBody': 'AI 会保留你写的事实和意思，改成规范的报告用语，并让中英文一致。你写的原文会被新版本替换。',
  'wording.fromObservation': '用 AI 观察生成报告语',
  'wording.fromObservationHint': 'AI 会把上面的观察写进这一项的报告语（在上面第 ② 栏）。检查结果仍以你选的为准。',
  'wording.fromObservationNeedsResult': '先在上面第 ① 栏选检查结果，再生成报告语。',
  'wording.overwriteTitle': '用 AI 重写覆盖你改过的报告语？',
  'wording.overwriteBody': '你写的报告语将被 AI 的新版本替换。',
  'wording.editHint': '可直接修改英文；保存后以你的文字为准。',
  'wording.editHintZh': '可直接改中文。改完点下面的「AI 按我写的重新生成」，英文会同步成规范的报告用语。',
  'wording.factsChanged': '事实已更改，请复核这句报告语。',
  'wording.source.ai': 'AI 写的',
  'wording.source.inspector': '你改过的',
  'wording.source.library': '模板句（尚未由 AI 写）',
  'wording.pendingStatus': '选择检查结果后才会有报告语。',
  'closeout.gapDefectNoFacts': '缺陷项还没有验屋师备注、AI 观察或报告语',
  'closeout.gapDefectNoWording': '缺陷项的报告语尚未写',
  'closeout.gapWordingEnglishNotSynced': '英文报告语还没按你改的中文同步',
  // 英文客户版不许夹中文（2026-10-16 正式验屋走查）：写给美国客户的报告里，验屋师手填的中文要改成英文。
  'closeout.gapClientEnglishChinese': '英文客户版会印出中文，请改写成英文',
  'closeout.gapClientEnglishChineseText': '{source}写的是「{text}」',
  'closeout.inspectionLevel': '验屋资料',
  'closeout.chineseTitle': '英文客户版里有中文',
  'closeout.chineseMeta': '客户拿到的英文报告会原样印出这些字，改成英文后才能出报告',
  'closeout.aiNote': 'AI 观察只是辅助，不会阻塞收尾。每个结果都是验屋师自己的判断。',
  // 离开前再看一眼（2026-10-27，AI 通宵评测 + 流程对标 A2 / B3）：只提醒、不阻塞报告——趁还在现场补拍、确认。
  'closeout.leaveTitle': '离开前再看一眼',
  'closeout.leaveMeta.one': '{n} 项 · 不阻塞报告',
  'closeout.leaveMeta.other': '{n} 项 · 不阻塞报告',
  'closeout.leaveBody': '趁还在现场补拍或确认，省得回头再跑一趟。',
  'closeout.leaveCount': '另有 {n} 项建议再看',
  'closeout.leaveShowAll': '再显示 {n} 项',
  'closeout.leaveAiFieldTest': 'AI：需现场测试，照片判断不了',
  'closeout.leaveAiUnclear': 'AI 从照片看不清',
  'closeout.leaveAiLowDefect': 'AI 疑似缺陷，但把握低',
  'closeout.leaveAiLow': 'AI 把握低',
  'closeout.leaveRowOpen': '这一项还没给结果',
  'closeout.leaveRowPass': '你选了无异常，可补近照或写备注',
  'closeout.leaveRowNoTest': '你选了无异常，但没记录测试',
  'closeout.leaveAiAsks': 'AI 问：{text}',
  'closeout.leaveAdvancedAsks': '高级 AI 建议补拍：{text}',
  'closeout.leaveAiQuestionTitle': 'AI 的问题',
  'closeout.leaveUnfiled.one': 'AI 还有 {n} 条发现没归位',
  'closeout.leaveUnfiled.other': 'AI 还有 {n} 条发现没归位',
  'closeout.leaveUnfiledBody': '在区域页归入检查项、记为其他问题或忽略',
  'closeout.leaveNewPhotos.one': 'AI 还没看过 {n} 张新照片',
  'closeout.leaveNewPhotos.other': 'AI 还没看过 {n} 张新照片',
  'closeout.leaveNewPhotosBody': '离开前可以在区域页再分析一次',
  'closeout.leaveAlarmTitle': '{level}的烟感 / CO 报警器',
  'closeout.leaveAlarmBody': '这一层的报警器项都还没选无异常或缺陷',
  'closeout.leaveNoPhoto': '有结果，但没挂照片',
  'closeout.leaveNoPhotoDefect': '缺陷项没挂照片',
  'aiTasks.empty': '还没有高级 AI 复核。可以在检查项里点「让高级 AI 再看」发起。',
  'findings.defectRow': '检查项缺陷',
  'findings.emptyBody': '选为「缺陷」的检查项和记录的其他发现会显示在这里。',
  // Owner device feedback 2026-09-14: report cover photo · inspector photo
  'cover.label': '报告封面',
  'cover.add': '拍封面照',
  'cover.fieldTitle': '报告封面照片',
  'cover.fieldBody': '拍一张房屋正面照，印在报告封面上；也会显示在验屋卡片和报告列表里。',
  'cover.pendingNote': '创建验屋后随记录一起保存。',
  'cover.options': '报告封面照片',
  'cover.view': '查看封面',
  'cover.replace': '更换封面',
  'cover.remove': '移除封面',
  'cover.removeTitle': '移除封面照片？',
  'cover.removeBody': '只从 App 里移除这张封面；系统相册里的原图不受影响。',
  'cover.viewer': '报告封面',
  'cover.locked': '报告已生成，封面不能再更换。',
  'cover.saveFailed': '封面照片保存失败：{message}',
  'profile.photo': '验屋师照片',
  'profile.photoBody': '显示在 App 右上角的头像位置。请用能看清面部的正面照。',
  'profile.photoRemove': '移除照片',
  'profile.photoRemoveTitle': '移除验屋师照片？',
  'profile.photoRemoveBody': '右上角会恢复为姓名首字。系统相册里的原图不受影响。',
  'profile.photoFailed': '照片保存失败：{message}',
  // Owner 选定设计稿 2026-09-14（Figma 191:3 / 191:7 / 191:10）：新建验屋四步、签署协议顶部验屋信息、地址联想。
  'setup.next': '下一步',
  'setup.stepOf': '步骤 {n} / {total} · {title}',
  'setup.step1Progress': '房屋信息',
  'setup.draftRestoredTitle': '已恢复上次未完成的填写',
  'setup.draftRestoredBody': '上次退出时填到一半的内容和封面照片都找回来了，可以接着填。',
  'setup.draftDiscard': '清空重填',
  'setup.step2Title': '客户 · 时间 · 验屋师',
  'setup.step4Title': '验屋协议 · 起步方式',
  'setup.step1Sub': '先确认这是一栋什么房子',
  'setup.step2Sub': '只填本次验屋的人员与时间',
  'setup.step3Sub': '确认将创建的区域',
  'setup.step4Sub': '签署可以稍后完成，但起步方式必须选',
  'setup.localityRow': '城市 · 州 · 邮编',
  'setup.localityTitle': '城市、州与邮编',
  'setup.coverSummary': '封面照片',
  'setup.missingJump': '点一下跳到缺的那一步',
  'agreement.signedBoth': '双方已完成签署',
  'address.suggestions': '地址建议',
  'address.poweredBy': 'Powered by Google',
  'common.edit': '编辑',
  'plan.areasCount.one': '{n} 个区域',
  'plan.areasCount.other': '{n} 个区域',
  // Owner 2026-10-20：没做完的一单只在一台设备上；用文件搬家；没用过的删除后退回。
  'inspectionStage.movedOut': '已搬走',
  'setup.deviceNote': '这一单要在这台设备上做完。想换设备继续，在「验屋」列表里用「搬到另一台设备」导出文件，再到另一台设备上导入。',
  'inspections.importFile': '从文件导入一单',
  'inspections.moveOut': '搬到另一台设备（导出文件）',
  'inspections.movedSection': '已搬到其他设备',
  'inspections.movedLine': '{date} 已导出 · 这里只能查看',
  'inspections.deleteReleaseNote': '这单还没出报告：如果没用过 AI，删除后会退回它用掉的 1 次免费验屋或单次购买（每 30 天最多 {limit} 次，现在还能退回 {left} 次）。用过 AI 的不退回。',
  'inspections.deleteMovedBody': '这是这一单搬到另一台设备后留在这里的副本。删除只删这台设备上的这份，另一台设备上的不受影响，也不会退回任何次数。',
  'release.released.trial': '已删除，退回了 1 次免费验屋。',
  'release.released.single': '已删除，退回了 1 次单次购买。',
  'release.kept.aiUsed': '已删除。这单用过 AI，用掉的次数不退回。',
  'release.kept.limitReached': '已删除。最近 30 天已经退回过 {limit} 次，这次不再退回。',
  'release.queued': '已删除。联网后会确认是否退回用掉的次数。',
  'moved.title': '这一单已搬到另一台设备',
  'moved.body': '{date} 导出到文件「{file}」。为了不让两台设备各改各的，这台设备上的这份现在只能查看。请在另一台设备上导入这个文件继续。',
  'moved.takenBy': '{device} 已经导入，正在那台设备上继续。',
  'moved.waiting': '还没有别的设备导入它。',
  'moved.undo': '撤销搬出，在这台设备继续',
  'moved.undoTitle': '在这台设备上继续这一单？',
  'moved.undoBody': '只有在别的设备还没导入这个文件时才能撤销。撤销后请把导出的文件删掉，不要再在任何设备上导入。',
  'moved.undoRefused': '{device} 已经导入这一单，请在那台设备上继续。要搬回来，在那台设备上再导出一次。',
  'moved.undone': '这一单又可以在这台设备上继续了。',
  'moved.deleteCopy': '删除这台设备上的副本',
  'move.title': '搬到另一台设备',
  'move.lockNote': '导出后，这台设备上的这份只能查看，不能再改。搬家不会再用掉免费验屋或购买次数。',
  'move.privacyNote': '文件里有客户资料和照片，导入后请把文件删掉。',
  'move.confirm': '导出文件',
  'move.working': '正在打包文件…',
  'move.done': '已导出「{file}」。请在另一台设备上导入它继续。',
  'move.cancelled': '没有导出，这一单仍在这台设备上。',
  'move.failed': '导出失败：{detail}',
  'move.blocked.aiRunning': 'AI 正在分析这一单，等它完成再导出。',
  'move.blocked.other': '这一单现在不能导出。',
  'move.filePrefix': '验屋搬家',
  'importFile.working': '正在导入…',
  'importFile.problem.notAFile': '这不是验屋师导出的验屋文件。',
  'importFile.problem.damaged': '文件不完整或已损坏（可能没传完，或被解压后又重新压缩过）。请在原来的设备上重新导出。',
  'importFile.problem.newerFormat': '这个文件是更新版本的 App 导出的，请先把这台设备上的 App 更新到最新版。',
  'importFile.problem.otherAccount': '这个文件是另一个账号导出的。请登录导出它的那个账号再导入。',
  'importFile.problem.alreadyHere': '这台设备上已经有这一单，而且可以继续做，不需要导入。',
  'importFile.problem.finished': '这一单已经出了报告，不能再导入。',
  'importFile.problem.notSignedIn': '请先登录再导入。',
  'importFile.problem.storageFull': '这台设备的存储空间不够，清理一些空间后再试。',
  'importFile.problem.failed': '导入失败：{detail}',
  'otherDevices.title.one': '另一台设备上还有 {n} 单没做完',
  'otherDevices.title.other': '另一台设备上还有 {n} 单没做完',
  'otherDevices.body': '{list}。请在开始它的那台设备上继续；想在这里做，先在那台设备上用「搬到另一台设备」导出文件，再在这里导入。',
  'otherDevices.item': '{device}，{date} 开始',
  'otherDevices.someDevice': '另一台设备',
  'settings.helpStep7': '换设备：没做完的一单只在开始它的那台设备上；账号、剩余次数和已出的报告在手机和电脑上通用。要换设备继续，在「验屋」列表里用「搬到另一台设备」导出文件，再到另一台设备上「从文件导入一单」。',
};
